import React, { useState, useEffect, useMemo, useRef } from 'react';
import Layout from "../../Component/Layout";
import AsyncCreatableSelect from "react-select/async-creatable";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import { ToastContainer, toast, Zoom } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import CharacteristicsModal from '../../Component/CharacteristicsModal ';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { MASTER_API, ORDER_API, PURCHASE_API } from "../../apiConfig";
import { useNavigate, useLocation } from "react-router-dom";
// Tolerance range calc is owned by the Product Configuration master
// (it defines the Over/Under Tolerances characteristics), so Enquiry
// just imports the shared function from there.
import { calculateToleranceRange } from "../../ProcurementManagement/ProductManagement/ProductConfiguration";
import { useFieldPermissions } from "../../utils/useFieldPermissions";
import { authHeaders } from "../../utils/authHeaders";
import MaskedValue from "../../Component/MaskedValue";
import { FileText } from 'lucide-react';


// --- Shared dropdown option/menu styling (matches Product Master dropdown look) ---
// A saved/calculated range like "25 - 25" (no tolerance % actually entered,
// so min === max) should just display as "25", not a fake range.
const collapseIfSameRange = (text) => {
    if (!text) return text;
    const parts = String(text).split('-').map(s => s.trim());
    if (parts.length === 2 && parts[0] !== '' && parts[1] !== '') {
        const min = parseFloat(parts[0]);
        const max = parseFloat(parts[1]);
        if (!isNaN(min) && !isNaN(max) && min === max) {
            return parts[0];
        }
    }
    return text;
};


// 🆕 This screen's own Module / Process / Sub Process — must exactly match
// what was picked in the User Manual upload screen (case-insensitive,
// trimmed) for the manual to be found.
const USER_MANUAL_SCOPE = {
    moduleName: "Order Management",
    process: "Sales",
    subProcess: "Enquiry"
};
const USER_MANUAL_LIST_API = `${MASTER_API}/user-manual/list`;

const dropdownOptionStyle = {
    control: (base) => ({
        ...base,
        minHeight: 32,
        height: 32,
        fontSize: '13px',
        borderColor: '#dee2e6',
        boxShadow: 'none',
        '&:hover': { borderColor: '#b3b3b3' }
    }),
    valueContainer: (base) => ({ ...base, padding: '0 8px', height: '32px', display: 'flex', alignItems: 'center' }),
    input: (base) => ({ ...base, margin: 0, padding: 0 }),
    dropdownIndicator: (base) => ({ ...base, padding: 4 }),
    menu: (base) => ({ ...base, fontSize: '13px', zIndex: 9999 }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected ? '#1e293b' : state.isFocused ? '#f1f5f9' : 'white',
        color: state.isSelected ? 'white' : '#334155',
        cursor: 'pointer',
        fontSize: '13px'
    })
};

// Reads module-level permissions (can_view / can_create) for the given
// module_id from the logged-in user's stored permissions, the same array
// authController.js returns at login. Admins get an empty `permissions`
// array from the backend by design, so they're granted full access here
// rather than being locked out by an "empty permissions" default.
const getModulePermissions = (moduleId) => {
    try {
        const user = JSON.parse(localStorage.getItem("user"));
        if (!user) return { canView: false, canCreate: false };
        if (user.role === "admin") return { canView: true, canCreate: true };

        const perm = (user.permissions || []).find((p) => p.module_id === moduleId);
        return {
            canView: perm?.can_view === 1,
            canCreate: perm?.can_create === 1
        };
    } catch (e) {
        console.error("Error reading user permissions:", e);
        return { canView: false, canCreate: false };
    }
};

function Enquiry() {
    // Role-based field visibility for this module. When the logged-in
    // user's role is Display-only, isFieldVisible(field_id) tells us
    // whether to show the real value or a masked placeholder in view mode.
    const { isFieldVisible, isFieldEditable, isDisplayOnly, isReviewOnly } = useFieldPermissions("SALES_INQUIRY");

    // Module-level access for this page. CREATE-type roles are add-only
    // (canView false, canCreate true) — they should see the create form
    // but not the existing-records list. DISPLAY-type roles are the
    // opposite (canView true, canCreate false) — they can browse/view but
    // the Create button and any mutating actions (delete) should be hidden.
    const { canView, canCreate } = useMemo(() => getModulePermissions("SALES_INQUIRY"), []);

    const [loading, setLoading] = useState(false);
    const [showManual, setShowManual] = useState(false);
    const [manualExists, setManualExists] = useState(null);
    const [manualFile, setManualFile] = useState(null);
    const [page, setPage] = useState(1);
    const [summaryData, setSummaryData] = useState([]);
    const [productList, setProductList] = useState([]);
    const [salesOrgList, setSalesOrgList] = useState([]);
    const [customerSegmentList, setCustomerSegmentList] = useState([]);
    const [channelList, setChannelList] = useState([]);
    const [allCustomerOptions, setAllCustomerOptions] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState("create");
    const [activeTab, setActiveTab] = useState("product");
    const [reviewComment, setReviewComment] = useState("");
    const [reviewSubmitting, setReviewSubmitting] = useState(false);

    // A CHANGE-role field that's unchecked in Role.js is read-only in Edit
    // mode (still visible, just disabled) — never locked in Create mode,
    // since Create isn't gated by per-field CHANGE config.
    const isFieldLocked = (fieldId) => modalMode === 'edit' && !isFieldEditable(fieldId);

    // A Review-only role never gets Product/Docs/Change Log — just the
    // Review tab. Push them there as soon as the modal opens on a record,
    // since the default activeTab ("product") isn't in their tab list.
    useEffect(() => {
        if (!isReviewOnly || modalMode === 'create') return;
        setActiveTab('review');
    }, [isReviewOnly, modalMode]);
    const [showCommentModal, setShowCommentModal] = useState(false);
    const [submitComment, setSubmitComment] = useState("");
    const [modalVisible, setModalVisible] = useState(false);
    const [selectedCharacteristics, setSelectedCharacteristics] = useState([]);
    const [modalIndex, setModalIndex] = useState(null);
    const [salesFile, setSalesFile] = useState(null);
    const [uploadMessage, setUploadMessage] = useState("");
    const [showExcelModal, setShowExcelModal] = useState(false);
    const [excelUploading, setExcelUploading] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [enquiryToDelete, setEnquiryToDelete] = useState(null);
    const salesFileInputRef = useRef(null);
    const selectedCharsRef = useRef([]);
    const [docFiles, setDocFiles] = useState([]);
    const docFileInputRef = useRef(null);
    const [enquiryLogs, setEnquiryLogs] = useState([]);
    const [enquiryId, setEnquiryId] = useState("");
    const [enquiryType, setEnquiryType] = useState("");
    const [salesOrg, setSalesOrg] = useState("");
    const [customerSegment, setCustomerSegment] = useState("");
    const [dChannel, setDChannel] = useState("");
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [selectedRefEnquiry, setSelectedRefEnquiry] = useState(null); // Reference Enquiry for create mode (copy-from)
    const [submittedBy, setSubmittedBy] = useState("");
    const [createdBy, setCreatedBy] = useState(""); // who actually created this enquiry (from DB on view, current user on create)
    const [project, setProject] = useState(""); // free-text project reference, shown beside Created Date
    const [items, setItems] = useState([]);
    const [filterSalesOrg, setFilterSalesOrg] = useState("");
    const [filterCustomerSegment, setFilterCustomerSegment] = useState("");
    const [filterDChannel, setFilterDChannel] = useState("");
    const [filterId, setFilterId] = useState("");
    const [filterCustomer, setFilterCustomer] = useState(null);
    const [enquiryStatus, setEnquiryStatus] = useState("");
    const [createdDate, setCreatedDate] = useState("");
    //eslint-disable-next-line
    const [approvedBy, setApprovedBy] = useState("");
    const [enquiryTypeList, setEnquiryTypeList] = useState([]);
    // 🆕 EXPORT ENQUIRY — mirrors SalesOrder.js's soOrderType==="export" flow.
    // Enquiry Type is the existing master dropdown (Configuration → Type of
    // Enquiry); when its value is "Export" (case-insensitive) we show a
    // Customer Country field and auto-fill Currency + Exchange Rate from
    // Exchange Rate Configuration the same way PO/SO already do.
    const isExportEnquiry = String(enquiryType || "").trim().toLowerCase() === "export";
    const [customerCountry, setCustomerCountry] = useState("");
    const [countryOptions, setCountryOptions] = useState([]);
    const [enqCurrency, setEnqCurrency] = useState("");
    const [enqExchangeRate, setEnqExchangeRate] = useState("");
    const [globalFilters, setGlobalFilters] = useState({
        construction: [], code: [], cableType: [], category: [],
        noOfCores: [], color: [], nominalCrossSection: [], referenceOfStandard: [], voltage: []
    });
    const [filteredProductList, setFilteredProductList] = useState(null);
    // --- FILTER DROPDOWN MASTER DATA ---
    const [filterMasterData, setFilterMasterData] = useState({
        constructions: [], codes: [], cableTypes: [], categories: [],
        cores: [], colours: [], nominalCrossSections: [], referenceOdStandards: [], voltages: []
    });

    // Helper to safely format API data into {label, value} for react-select
    const getFilterOptions = (list) => {
        if (!list || !Array.isArray(list)) return [];
        return list.map(item => {
            if (typeof item === 'string') return { value: item, label: item };
            // Check standard variations of field names your backend uses
            const val = item.name || item.code || item.cable_type || item.category || item.core || item.colour || item.value || "";
            return { value: String(val).trim(), label: String(val).trim() };
        }).filter(opt => opt.value !== "");
    };
    // -----------------------------
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {

        fetchSummary();
        fetchProductList();
        fetchMasterData();
        loadCustomerOptions("");

        const getCurrentUser = () => {
            const userObj = localStorage.getItem("user");
            if (userObj) {
                try {
                    const parsedUser = JSON.parse(userObj);
                    // Prefer the user's full name (first + last, from Create User /
                    // Account Settings) so Created By / Updated By show a real name.
                    // Falls back to the combined name, then user id, then email for
                    // accounts that don't have a name saved yet.
                    const fullName = `${parsedUser.firstName || ''} ${parsedUser.lastName || ''}`.trim();
                    if (fullName) return fullName;
                    if (parsedUser.name && parsedUser.name.trim()) return parsedUser.name.trim();
                    if (parsedUser.userId) return parsedUser.userId;
                    if (parsedUser.email) return parsedUser.email;
                } catch (e) { console.error("Error parsing user data:", e); }
            }
            const storedUserId = localStorage.getItem("userId");
            if (storedUserId && storedUserId !== "undefined" && storedUserId !== "null") return storedUserId;
            const email = localStorage.getItem("email");
            if (email && email !== "undefined" && email !== "null") return email;
            const storedName = localStorage.getItem("userName");
            if (storedName && storedName !== "undefined" && storedName !== "null") return storedName;
            return "User";
        };

        setSubmittedBy(getCurrentUser());

        // ✅ OPEN ENQUIRY FROM DASHBOARD
        if (
            location.state &&
            location.state.inquiryId
        ) {

            handleOpenView(location.state.inquiryId);


        }
        // eslint-disable-next-line react-hooks/exhaustive-deps        
    }, []);

    useEffect(() => {
        selectedCharsRef.current = selectedCharacteristics;
    }, [selectedCharacteristics]);

    // Re-fetch the cascading filter dropdown data any time a filter
    // selection changes (including on mount, when globalFilters is the
    // default all-empty object) — this is what makes every field aware
    // of every other field's current selection.
    useEffect(() => {
        fetchProductFilterOptions(globalFilters);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [globalFilters]);

    const fetchSummary = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${ORDER_API}/enquiry/summary`, { headers: authHeaders() });
            const data = await res.json();
            // An error reply (e.g. 403 "Not authorized") is an object, not a
            // list. Storing it made summaryData.filter crash the whole page.
            if (!res.ok || !Array.isArray(data)) {
                setSummaryData([]);
                toast.error(data?.message || data?.error || "Failed to load inquiries");
                return;
            }
            setSummaryData(data);
        } catch (err) { toast.error("Failed to load inquiries"); }
        finally { setLoading(false); }
    };

    const fetchProductList = async () => {
        try {
            const res = await fetch(`${ORDER_API}/rfq/fetch-product-list`, { headers: authHeaders() });
            const data = await res.json();

            console.log("PRODUCT LIST RAW:", data);

            let products = [];
            if (Array.isArray(data)) {
                products = data;
            } else if (Array.isArray(data.products)) {
                products = data.products;
            }

            const formatted = products.map(item => {
                // Log the first item so you can see ALL real field names from the API
                if (products.indexOf(item) === 0) {
                    console.log("PRODUCT ITEM KEYS:", Object.keys(item));
                    console.log("PRODUCT ITEM SAMPLE:", item);
                }

                return {
                    value: item.product_code || item.Product_Code || item.value,
                    label: (() => {
                        const code = item.product_code || item.Product_Code || item.value || '';
                        const desc = item.Description || item.description || '';
                        return desc ? `${code} - ${desc}` : code;
                    })(),
                    description: item.Description || item.description || '',
                    uom: item.UOM || item.uom || '',
                    product_class: item.Product_Class || item.product_class || '',

                    // --- FILTER FIELDS: covers snake_case, camelCase, PascalCase ---
                    construction:
                        item.construction || item.Construction || '',

                    code:
                        item.code || item.Code || '',

                    cableType:
                        item.cableType || item.cable_type || item.CableType || item.Cable_Type || '',

                    category:
                        item.productCategory || item.product_category ||
                        item.category || item.Category || '',

                    noOfCores:
                        item.noOfCores || item.no_of_cores || item.NoOfCores ||
                        item.No_Of_Cores || item.cores || item.Cores || '',

                    color:
                        item.color || item.Color || item.colour || item.Colour || '',

                    nominalCrossSection:
                        item.nominalCrossSection || item.nominal_cross_section ||
                        item.NominalCrossSection || item.Nominal_Cross_Section || '',

                    referenceOfStandard:
                        item.referenceOfStandard || item.reference_of_standard ||
                        item.ReferenceOfStandard || item.Reference_Of_Standard ||
                        item.referenceOdStandard || '',

                    voltage:
                        item.voltage || item.Voltage || '',
                };
            });

            console.log("FORMATTED SAMPLE:", formatted[0]);
            setProductList(formatted);

        } catch (error) {
            console.error("Error fetching product list", error);
        }
    };

    const fetchMasterData = async () => {
        try {
            // Existing Enquiry Header Data (unrelated to product filters — unchanged)
            const resOrg = await fetch(`${MASTER_API}/configuration/sales-organization`, { headers: authHeaders() });
            const resSeg = await fetch(`${MASTER_API}/configuration/dynamic/customer-segment`, { headers: authHeaders() });
            const resChn = await fetch(`${MASTER_API}/configuration/distribution-channel`, { headers: authHeaders() });
            const resEnquiryType = await fetch(`${MASTER_API}/configuration/dynamic/type-of-enquiry`, { headers: authHeaders() });

            if (resOrg.ok) setSalesOrgList(await resOrg.json());
            if (resSeg.ok) setCustomerSegmentList(await resSeg.json());
            if (resChn.ok) setChannelList(await resChn.json());
            if (resEnquiryType.ok) setEnquiryTypeList(await resEnquiryType.json());

            // 🆕 Country master (for Export enquiry's Customer Country dropdown)
            // — same config source as SalesOrder.js's Export SO field, so it
            // can never drift from the GST Configuration screen's Country master.
            fetch(`${MASTER_API}/configuration/country`, { headers: authHeaders() })
                .then(r => r.json())
                .then(d => {
                    const raw = d.data || d || [];
                    setCountryOptions(Array.isArray(raw) ? raw : []);
                })
                .catch(err => console.error("Error fetching country list:", err));

        } catch (err) {
            console.error("Error fetching master data:", err);
        }
    };

    // 🆕 Auto-fill Currency + Exchange Rate the moment a Customer Country is
    // picked on an Export enquiry, using the same Exchange Rate Configuration
    // table SalesOrder.js's fetchExchangeRateForCustomerCountry reads.
    // Non-Export enquiries (or no country yet) clear both.
    const ENQ_HOME_COUNTRY = "India";
    const fetchExchangeRateForCustomerCountry = async (country) => {
        if (!country || !isExportEnquiry) {
            setEnqCurrency("");
            setEnqExchangeRate("");
            return;
        }

        const url = `${PURCHASE_API}/exchange-rate-config?from_country=${encodeURIComponent(ENQ_HOME_COUNTRY)}&to_country=${encodeURIComponent(country)}`;
        try {
            const res = await fetch(url, { headers: authHeaders() });
            const contentType = res.headers.get("content-type") || "";
            if (!res.ok || !contentType.includes("application/json")) {
                console.error(`Exchange rate config request failed: ${res.status} for ${url}`);
                toast.error(`Exchange rate service returned ${res.status}. Enter Currency / Rate manually.`);
                setEnqCurrency("");
                setEnqExchangeRate("");
                return;
            }

            const data = await res.json();
            const rows = Array.isArray(data) ? data : (data.data || []);
            if (!rows.length) {
                toast.error(`No exchange rate configured for ${ENQ_HOME_COUNTRY} → ${country}. Enter Currency / Rate manually.`);
                setEnqCurrency("");
                setEnqExchangeRate("");
                return;
            }

            const match = rows[0]; // latest available (sorted year/month DESC server-side)
            setEnqCurrency(match.to_currency || "");
            setEnqExchangeRate(Number(match.exchange_rate) || "");
        } catch (err) {
            console.error(`Error fetching exchange rate config from ${url}:`, err);
            toast.error("Failed to fetch exchange rate for the selected Customer Country. See console for the failing URL.");
        }
    };

    // Re-fetch whenever Enquiry Type or Customer Country changes.
    useEffect(() => {
        fetchExchangeRateForCustomerCountry(customerCountry);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customerCountry, isExportEnquiry]);

    // --- Turn the currently-selected globalFilters into a query string ---
    // e.g. { color: [{value:'Red'}], category: [{value:'Power Cable'}] }
    //   -> "color=Red&category=Power+Cable"
    const buildFilterQuery = (filters) => {
        const params = new URLSearchParams();
        Object.entries(filters || {}).forEach(([key, selectedOpts]) => {
            if (Array.isArray(selectedOpts) && selectedOpts.length > 0) {
                const values = selectedOpts.map(o => (o && o.value !== undefined) ? o.value : o);
                params.set(key, values.join(","));
            }
        });
        return params.toString();
    };

    // --- Cascading filter dropdown data — all 9 fields now come from a
    // single productDetails-backed endpoint, scoped to productType =
    // 'Finished Goods' and narrowed by whatever's currently selected in the
    // OTHER filters. Call this any time globalFilters changes. ---
    const fetchProductFilterOptions = async (filters = globalFilters) => {
        try {
            const qs = buildFilterQuery(filters);
            const res = await fetch(`${ORDER_API}/enquiry/product-filters${qs ? `?${qs}` : ""}`, { headers: authHeaders() });
            if (!res.ok) return;
            const data = await res.json();
            setFilterMasterData(data);
        } catch (err) {
            console.error("Error fetching product filter options:", err);
        }
    };

    const loadCustomerOptions = async (inputValue) => {
        try {
            const res = await fetch(`${ORDER_API}/rfq/customers`, { headers: authHeaders() });
            const data = await res.json();
            if (!res.ok || !Array.isArray(data)) return []; // e.g. 403 -> no options, no crash
            const allOptions = data.map(c => ({
                value: c.partner_code,
                label: `${c.partner_code} - ${c.vendor_name}`,
            }));
            setAllCustomerOptions(allOptions);
            if (!inputValue) return allOptions;
            const query = inputValue.toLowerCase();
            const matched = allOptions.filter(o => o.label.toLowerCase().includes(query));
            const unmatched = allOptions.filter(o => !o.label.toLowerCase().includes(query));
            return [...matched, ...unmatched];
        } catch (err) { return []; }
    };

    const handleSalesFileSave = async () => {
        if (!salesFile) { toast.error("Please select an Excel file."); return; }
        const formData = new FormData();
        formData.append("file", salesFile);
        setExcelUploading(true);
        try {
            const res = await fetch(`${ORDER_API}/enquiry/parse-excel`,{ headers: authHeaders(), method: "POST", body: formData });
            if (!res.ok) throw new Error(await res.text());
            const data = await res.json();
            setUploadMessage("File parsed successfully.");

            // Backend already:
            //   - overwrites description/uom/unit_price/product_class/characteristics from DB for known products
            //   - passes Excel values through for unknown products (no class/characteristics)
            //   - computes quantity = factor * length
            const mappedData = data.data.map(row => ({
                product_code: row.product_code || "",
                description: row.description || "",
                uom: row.uom || "",
                quantity: row.quantity || 0,
                unit_price: row.unit_price || 0,
                req_date: row.req_date || "",
                total_price: row.total_price || "0.00",
                product_class: row.product_class || "",
                characteristics: row.characteristics || [],
                _fromRecords: row._fromRecords || false,
                factor: row.factor || 0,
                length: row.length || 0,
            }));

            setItems([...items, ...mappedData]);
            toast.success("File uploaded successfully!");
            setShowExcelModal(false);
            setSalesFile(null);
        } catch (err) { toast.error("Failed to parse file"); }
        finally { setExcelUploading(false); }
    };

    // --- Enquiry document upload size limit — configurable via
    // Configuration → File Size Config (Module = "Enquiry").
    // Falls back to 50 KB (the old hardcoded value) if it can't be fetched.
    const DEFAULT_MAX_ENQUIRY_DOC_SIZE_KB = 50;
    const [maxEnquiryDocSizeKb, setMaxEnquiryDocSizeKb] = useState(DEFAULT_MAX_ENQUIRY_DOC_SIZE_KB);

    useEffect(() => {
        fetch(`${MASTER_API}/configuration/file-size-limit/Enquiry`, { headers: authHeaders() })
            .then(res => res.ok ? res.json() : Promise.reject())
            .then(data => {
                if (data && data.maxSizeKb) setMaxEnquiryDocSizeKb(data.maxSizeKb);
            })
            .catch(() => { /* keep default limit if config fetch fails */ });
    }, []);

const filterFilesWithinSizeLimit = (files) => {
    const maxBytes = maxEnquiryDocSizeKb * 1024;
    const okFiles = [];
    const rejected = [];
    files.forEach(f => {
        if (f.size > maxBytes) {
            rejected.push(f.name);
        } else {
            okFiles.push(f);
        }
    });
    if (rejected.length) {
        toast.error(
            `${rejected.length} file(s) exceed the ${maxEnquiryDocSizeKb} KB upload limit and were skipped: ${rejected.join(', ')}`
        );
    }
    return okFiles;
};


const getFileIcon = (name = '', type = '') => {
    const ext = (name.split('.').pop() || '').toLowerCase();
    if (['pdf'].includes(ext)) return '📄';
    if (['xls', 'xlsx', 'csv'].includes(ext)) return '📊';
    if (['doc', 'docx'].includes(ext)) return '📝';
    if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) return '🖼️';
    if (['zip', 'rar', '7z'].includes(ext)) return '🗜️';
    return '📎';
};
    const handleDownloadTemplate = () => {
        const a = document.createElement("a");
        a.href = "/assets/files/enquiry_template.xlsx";
        a.download = "enquiry_template.xlsx";
        a.click();
    };

    const handleOpenCreate = () => {
        setModalMode("create");
        setEnquiryId(""); setEnquiryType(""); setSelectedCustomer(null); setSalesOrg("");
        setCustomerCountry(""); setEnqCurrency(""); setEnqExchangeRate("");
        setCustomerSegment(""); setDChannel(""); setSubmitComment("");
        setItems([]); setActiveTab("product"); setShowModal(true);
        setSalesFile(null); setUploadMessage(""); setEnquiryLogs([]); setDocFiles([]);
        setCreatedDate(formatDateLocal(new Date()));
        setApprovedBy("");
        setCreatedBy(submittedBy); // new enquiry → creator is the current user
        setSelectedRefEnquiry(null);
        setProject("");
    };

    // Reference Enquiry (create mode only) — pick an existing enquiry to copy
    // its header + product rows into the blank Create form. Mirrors the RFQ
    // page's "Reference RFQ" copy-from behavior. Never carries over the row
    // PK (`id`), enquiry_id, or status — this is always producing a new,
    // independent Draft enquiry.
    const handleRefEnquirySelect = async (selectedOption) => {
        setSelectedRefEnquiry(selectedOption);
        if (!selectedOption) {
            setSelectedCustomer(null); setSalesOrg(""); setCustomerSegment(""); setDChannel("");
            setEnquiryType(""); setItems([]);
            return;
        }
        try {
            const res = await fetch(`${ORDER_API}/enquiry/${selectedOption.value}`, { headers: authHeaders() });
            if (!res.ok) throw new Error("Failed to fetch reference enquiry");
            const rows = await res.json();
            if (!rows.length) { toast.error("Reference enquiry has no data"); return; }

            const header = rows[0];
            setEnquiryType(header.enquiry_type || "");
            setSalesOrg(header.sales_organization || "");
            setCustomerSegment(header.customer_segment || "");
            setDChannel(header.distribution_channel || "");
            setProject(header.project || "");
            if (header.customer) setSelectedCustomer({ label: header.customer, value: header.customer });

            const mappedItems = rows.map(r => ({
                product_code: r.product_code,
                description: r.description,
                uom: r.uom,
                quantity: r.quantity,
                unit_price: r.unit_price,
                req_date: r.requirement_date
                    ? (() => {
                        const s = String(r.requirement_date);
                        if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
                        return s.substring(0, 10);
                    })()
                    : "",
                total_price: r.total_price,
                product_class: r.product_class,
                currency: r.currency || "",
                range: collapseIfSameRange(r.range_qty) || "",
                characteristics: normalizeCharacteristics(r.characteristics),
                _fromRecords: false
            }));
            setItems(mappedItems);
            toast.success(`Reference Enquiry ${selectedOption.value} loaded!`);
        } catch (err) {
            console.error("handleRefEnquirySelect error:", err);
            toast.error("Failed to load reference enquiry data");
        }
    };

    const handleDeleteEnquiry = (id) => {
        setEnquiryToDelete(id);
        setShowDeleteConfirm(true);
    };

    const confirmDeleteEnquiry = async () => {
        const id = enquiryToDelete;
        setShowDeleteConfirm(false);
        if (!id) return;

        try {
            const res = await fetch(`${ORDER_API}/enquiry/delete/${id}`,{ headers: authHeaders(), method: "PUT" });
            if (!res.ok) throw new Error("Failed to delete");
            toast.success("Enquiry deleted successfully");
            fetchSummary();
        } catch (err) {
            toast.error("Failed to delete enquiry");
        } finally {
            setEnquiryToDelete(null);
        }
    };

    const handleOpenView = async (id) => {
        try {
            const res = await fetch(`${ORDER_API}/enquiry/${id}`, { headers: authHeaders() });
            if (!res.ok) throw new Error("Failed to fetch");
            const rows = await res.json();

            // Fetch change logs in parallel
            const logRes = await fetch(`${ORDER_API}/enquiry/change-logs/${id}`, { headers: authHeaders() });
            const logs = logRes.ok ? await logRes.json() : [];
            setEnquiryLogs(logs);

            // Fetch uploaded docs for this enquiry
            try {
                const docsRes = await fetch(`${ORDER_API}/enquiry/list-docs/${id}`, { headers: authHeaders() });
                const docsData = docsRes.ok ? await docsRes.json() : { files: [] };
                setDocFiles((docsData.files || []).map(f => ({
    name: f.originalname,
    filename: f.filename,
    googleFileId: f.googleFileId,
    webViewLink: f.webViewLink,
    size: f.size,
    type: f.mimetype || "",
    uploadedAt: f.uploadedAt || "",
    enquiryId: id
})));
            } catch (_) { setDocFiles([]); }

            if (rows.length > 0) {
                const header = rows[0];
                setEnquiryId(header.enquiry_id);
                setEnquiryType(header.enquiry_type || "");
                // 🆕 Restore Export enquiry fields (blank/cleared for a Domestic enquiry)
                setCustomerCountry(header.customer_country || "");
                setEnqCurrency(header.currency && String(header.enquiry_type || "").trim().toLowerCase() === "export" ? header.currency : "");
                setEnqExchangeRate(header.exchange_rate || "");
                setSelectedCustomer({ label: header.customer, value: header.customer });
                setSalesOrg(header.sales_organization);
                setCustomerSegment(header.customer_segment || "");
                setDChannel(header.distribution_channel);
                setSubmitComment(header.comment || "");
                setEnquiryStatus(header.status || "");
                // UPDATE THIS BLOCK:
                setCreatedDate(
                    header.created_date
                        ? formatDateLocal(header.created_date)
                        : header.created_at
                            ? formatDateLocal(header.created_at)
                            : ""
                );
                setApprovedBy(header.approved_by || "");
                setCreatedBy(header.created_by || "");
                setProject(header.project || "");

                const mappedItems = rows.map(r => ({
                    id: r.id,                          // ← row PK needed for UPDATE by id
                    product_code: r.product_code,
                    description: r.description,
                    uom: r.uom,
                    quantity: r.quantity,
                    unit_price: r.unit_price,
                    req_date: r.requirement_date
                        ? (() => {
                            const s = String(r.requirement_date);
                            // Already a plain date string like "2026-06-18"
                            if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
                            // ISO string like "2026-06-18T00:00:00.000Z" — extract date part only
                            return s.substring(0, 10);
                        })()
                        : "",
                    total_price: r.total_price,
                    product_class: r.product_class,
                    currency: r.currency || "",
                    range: collapseIfSameRange(r.range_qty) || "",
                    characteristics: normalizeCharacteristics(r.characteristics),
                    _fromRecords: true
                }));
                setItems(mappedItems);
            }
            setModalMode("view"); setActiveTab("product"); setShowModal(true);
        } catch (err) { toast.error("Could not load Enquiry details"); }
    };

    const handleCloseModal = () => {

        setShowModal(false);
        setEnquiryStatus("");
        if (
            location.state &&
            location.state.from === "dashboard"
        ) {

            navigate("/cable/sales/inquiry-dashboard");
        }
    };

    const handleAddItem = () => {
        setItems([...items, {
            product_code: "", description: "", uom: "", req_date: "",
            quantity: "", unit_price: "", total_price: 0,
            product_class: "", currency: "", characteristics: [], _fromRecords: false
        }]);
    };

    const handleRemoveItem = (index) => {
        const newItems = [...items];
        newItems.splice(index, 1);
        setItems(newItems);
    };

    const handleItemChange = (index, field, value) => {
        const newItems = [...items];
        newItems[index][field] = value;

        if (field === 'quantity' || field === 'unit_price') {
            const qty = parseFloat(newItems[index].quantity) || 0;
            const price = parseFloat(newItems[index].unit_price) || 0;
            newItems[index].total_price = (qty * price).toFixed(2);
        }
        setItems(newItems);
    };

    const handleProductChange = async (index, selectedOption) => {

        if (!selectedOption) return;

        const val = selectedOption.value;

        try {

            const res = await fetch(`${ORDER_API}/rfq/fetch-product-details?productCode=${val}`, { headers: authHeaders() });

            const response = await res.json();

            console.log("STANDARD PRICE =", response.data?.data?.[0]?.Standard_Price);

            const p =
                Array.isArray(response)
                    ? response[0]
                    : response?.data?.[0];

            if (res.ok && p) {

                const updatedItems = [...items];

                updatedItems[index] = {
                    ...updatedItems[index],
                    _fromRecords: true,
                    product_code: val,

                    description:
                        p.Description || "",

                    uom:
                        p.UOM || "",

                    unit_price:
                        // 🆕 Export enquiry: fold the Customer-Country exchange rate
                        // into the price shown/saved for this line — mirrors
                        // SalesOrder.js's finalUnitPrice for an Export SO
                        // (basePrice * exchange_rate, markup omitted here since
                        // Enquiry has no markup concept). Rounded to 2 decimals
                        // so the grid/input doesn't show long float noise
                        // (e.g. 0.961339999999...).
                        isExportEnquiry
                            ? Number(((Number(p.Standard_Price) || 0) * (Number(enqExchangeRate) || 1)).toFixed(2))
                            : (Number(p.Standard_Price) || 0),

                    product_class:
                        p.Product_Class || "",

                    currency:
                        // 🆕 Export enquiry: line currency follows the enquiry's own
                        // (Customer Country -> Exchange Rate Configuration) currency
                        // instead of the product master's default.
                        isExportEnquiry ? (enqCurrency || p.Currency || "") : (p.Currency || ""),

                    characteristics:
                        p.Characteristics || [],

                    total_price:
                        (
                            (parseFloat(updatedItems[index].quantity) || 0) *
                            (isExportEnquiry
                                ? Number(((Number(p.Standard_Price) || 0) * (Number(enqExchangeRate) || 1)).toFixed(2))
                                : (Number(p.Standard_Price) || 0))
                        ).toFixed(2)
                };

                console.log("UPDATED ITEM:", updatedItems[index]);

                setItems(updatedItems);
            }

        } catch (e) {

            console.error("PRODUCT FETCH ERROR:", e);
        }
    };

    // "Type of Packing" is saved per product line item (like "No. of Drum/Coil"
    // / "Drum Length"), through the same generic characteristics table keyed by
    // this item's row id. This injects a "Type of Packing" entry right after
    // the Drum/Coil length characteristic if it isn't already on the row, so
    // it shows up (and gets saved) through the same Fast Entry / characteristics
    // modal — mirrors the Sales Order / RFQ Fast Entry behavior.
    // Converts characteristics coming back from the DB (JSON string or array,
    // tolerance_applied as 0/1/"0"/"1") into a consistent array with a real
    // boolean tolerance_applied — this is what makes the Over/Under Tolerances
    // checkbox stay exactly as the user left it after Save/Update or a reopen,
    // instead of silently resetting to unchecked once data round-trips
    // through the backend (which previously never stored this flag at all).
    const normalizeCharacteristics = (raw) => {
        const list = typeof raw === 'string'
            ? (raw ? JSON.parse(raw) : [])
            : (raw || []);
        return list.map(c => ({
            ...c,
            tolerance_applied: c.tolerance_applied === true
                || c.tolerance_applied === 1
                || c.tolerance_applied === "1"
        }));
    };

    const TYPE_OF_PACKING_LABEL = "Type of Packing";
    const withTypeOfPacking = (charList, fallbackValue = "") => {
        const list = [...(charList || [])];
        const alreadyPresent = list.some(
            (c) => (c.characteristic || "").trim().toLowerCase() === "type of packing"
        );
        if (alreadyPresent) return list;

        const drumCoilLengthIdx = list.findIndex((c) => {
            const name = (c.characteristic || "").toLowerCase();
            return name.includes("length") && (name.includes("drum") || name.includes("coil"));
        });

        const topEntry = {
            characteristic: TYPE_OF_PACKING_LABEL,
            value: fallbackValue || "",
            calculation_type: "",
        };

        if (drumCoilLengthIdx === -1) {
            list.push(topEntry);
        } else {
            list.splice(drumCoilLengthIdx + 1, 0, topEntry);
        }
        return list;
    };

    // Characteristic names that make up one "Drum/Coil" group — duplicated as
    // a set by the modal's "+ Add" button (handleAddDrumGroup below) and
    // reconstructed as repeated groups when an enquiry with multiple groups
    // is reopened (see handleOpenCharModal's existingByName/extra-groups
    // logic just below).
    const DRUM_GROUP_NAMES = [
        "No. of Drum/Coil",
        "Length of the Drum/Coil",
        "Over Tolerance",
        "Under Tolerance",
        "Type of Packing"
    ];

 const handleOpenCharModal = async (index, productData) => {
    setModalIndex(index);

    try {
        // ================= VIEW MODE =================
        if (modalMode === "view") {
            const savedChars = normalizeCharacteristics(
                productData.characteristics || []
            );

            try {
                const res = await fetch(
                    `${ORDER_API}/rfq/fetch-product-details?productCode=${productData.product_code}`,
                    { headers: authHeaders() }
                );

                const response = await res.json();

                const p = Array.isArray(response)
                    ? response[0]
                    : response?.data?.[0];

                const masterChars = p?.Characteristics || [];

                const masterByName = {};

                masterChars.forEach(mc => {
                    masterByName[mc.characteristic] = mc;
                });

                const merged = savedChars.map(c => ({
                    ...c,
                    tolerance_checkbox:
                        masterByName[c.characteristic]?.tolerance_checkbox ??
                        c.tolerance_checkbox
                }));

                setSelectedCharacteristics(
                    withTypeOfPacking(
                        merged,
                        productData.type_of_packing || ""
                    )
                );

            } catch (masterErr) {
                console.error(
                    "View-mode master fetch error:",
                    masterErr
                );

                setSelectedCharacteristics(
                    withTypeOfPacking(
                        savedChars,
                        productData.type_of_packing || ""
                    )
                );
            }

            setModalVisible(true);
            return;
        }

        // ================= CREATE / EDIT =================

        const res = await fetch(
            `${ORDER_API}/rfq/fetch-product-details?productCode=${productData.product_code}`,
            { headers: authHeaders() }
        );

        const response = await res.json();

        const p = Array.isArray(response)
            ? response[0]
            : response?.data?.[0];

        const masterChars = p?.Characteristics || [];

        // Get ALL saved rows.
        // IMPORTANT:
        // Do not group by characteristic name.
        // This preserves duplicate Drum/Coil groups.
        const savedChars = normalizeCharacteristics(
            productData.characteristics || []
        );

        // Product Master metadata lookup
        const masterByName = {};

        masterChars.forEach(mc => {
            const key = (mc.characteristic || "")
                .trim()
                .toLowerCase();

            masterByName[key] = mc;
        });

        // Merge master metadata into every saved row.
        // Every saved row is preserved.
        const mergedSavedChars = savedChars.map(saved => {
            const key = (saved.characteristic || "")
                .trim()
                .toLowerCase();

            const master = masterByName[key];

            return {
                ...(master || {}),
                ...saved,

                // Keep saved value
                value: saved.value ?? "",

                // Keep tolerance checkbox state
                tolerance_applied:
                    !!saved.tolerance_applied,

                // Keep calculation type
                calculation_type:
                    saved.calculation_type ??
                    master?.calculation_type ??
                    "",

                // Keep tolerance checkbox configuration
                tolerance_checkbox:
                    saved.tolerance_checkbox ??
                    master?.tolerance_checkbox
            };
        });

        // Keep all rows exactly as saved.
        // Only add Type of Packing if required.
        const finalChars = withTypeOfPacking(
            mergedSavedChars,
            productData.type_of_packing || ""
        );

        setSelectedCharacteristics(finalChars);

        setModalVisible(true);

    } catch (err) {
        console.error(
            "Characteristic fetch error:",
            err
        );

        // Fallback if Product Master fetch fails
        setSelectedCharacteristics(
            withTypeOfPacking(
                productData.characteristics || [],
                productData.type_of_packing || ""
            )
        );

        setModalVisible(true);
    }
};

    // Over/Under Tolerances checkboxes are linked — checking either one
    // checks both, since Range is only ever calculated using both together.
    const handleToggleTolerance = (index) => {
        const target = selectedCharacteristics[index];
        const name = (target?.characteristic || '').trim().toLowerCase();
        if (name !== 'over tolerance' && name !== 'under tolerance') return;

        const newChecked = !target.tolerance_applied;
        const u = selectedCharacteristics.map((c) => {
            const cName = (c.characteristic || '').trim().toLowerCase();
            if (cName === 'over tolerance' || cName === 'under tolerance') {
                return { ...c, tolerance_applied: newChecked };
            }
            return c;
        });
        setSelectedCharacteristics(u);
    };

    const handleAddDrumGroup = () => {
        setSelectedCharacteristics(prev => {
            // Was: match each of DRUM_GROUP_NAMES's 5 hardcoded strings
            // against prev by exact (trimmed, lowercased) name, and only
            // clone the ones that matched. Any characteristic whose stored
            // name didn't exact-match the whitelist — even by a stray space
            // or punctuation difference — was silently skipped, so "+ Add"
            // could clone 1 field and quietly drop the other 4 with no error.
            //
            // Now: clone the first occurrence of EVERY characteristic
            // currently on the row, whatever it's named. This can never
            // silently drop a field — whatever's shown gets duplicated.
            const seen = new Set();
            const baseEntries = [];
            prev.forEach((c) => {
                const key = (c.characteristic || "").trim().toLowerCase();
                if (!seen.has(key)) {
                    seen.add(key);
                    baseEntries.push(c);
                }
            });
            if (baseEntries.length === 0) return prev;
            const clones = baseEntries.map(t => ({
                ...t,
                value: "",
                tolerance_applied: false
            }));
            return [...prev, ...clones];
        });
    };

    // "Delete" icon in the Characteristics (Fast Entry) modal — removes one
    // Drum/Coil GROUP (the group at groupIdx: every characteristic's
    // groupIdx-th occurrence, same "occurrence index = group index"
    // convention handleAddDrumGroup relies on above). Mirrors
    // handleAddDrumGroup's grouping logic in reverse.
    const handleRemoveDrumGroup = (groupIdx) => {
        setSelectedCharacteristics(prev => {
            const byName = {};
            const order = [];
            prev.forEach((c) => {
                const key = (c.characteristic || "").trim().toLowerCase();
                if (!byName[key]) {
                    byName[key] = [];
                    order.push(key);
                }
                byName[key].push(c);
            });

            // Never delete the last remaining group — a line item must
            // always keep at least one Drum/Coil group.
            const groupCount = Math.max(0, ...order.map(k => byName[k].length));
            if (groupCount <= 1) {
                toast.info("At least one Drum/Coil group is required.", { autoClose: 2000, hideProgressBar: true });
                return prev;
            }

            order.forEach((key) => {
                if (byName[key][groupIdx] !== undefined) {
                    byName[key].splice(groupIdx, 1);
                }
            });

            const rebuilt = [];
            order.forEach((key) => {
                byName[key].forEach((c) => rebuilt.push(c));
            });
            return rebuilt;
        });
    };

    // AFTER
    const handleSaveCharModal = (newChars) => {
        console.log("newChars:", newChars);

        // Dynamically find all characteristics marked for quantity-based calculation.
        // calculation_type may be a comma-separated string like "Quantity Based Calculation,Match"
        //
        // A "+ Add" click (handleAddDrumGroup) appends a full duplicate set of
        // Drum/Coil characteristics with the SAME characteristic names as the
        // originals, so a name like "No. of Drum/Coil" can now appear more than
        // once in newChars — one occurrence per group the user added. Group
        // qty-based characteristics by name, in the order they appear, so the
        // 1st occurrence of every qty-based name forms "group 0", the 2nd
        // occurrence forms "group 1", etc. Each group's values are multiplied
        // together (e.g. No. of Drum/Coil × Length of the Drum/Coil for that
        // group), and the groups' products are then summed — e.g. two groups
        // of (8, 100) and (2, 100) give (8*100) + (2*100) = 1000, instead of
        // flattening everything into one product (8*2*100*100).
        //
        // A qty-based characteristic that only appears once (no duplicate
        // group for it) is broadcast — its single value is reused as that
        // characteristic's factor in every group's product.
        const qtyByName = {};
        newChars.forEach(c => {
            if (!c.calculation_type) return;
            const types = c.calculation_type.split(",").map(s => s.trim());
            if (!types.includes("Quantity Based Calculation")) return;
            const key = (c.characteristic || "").trim().toLowerCase();
            if (!qtyByName[key]) qtyByName[key] = [];
            qtyByName[key].push(c.value);
        });

        const qtyNames = Object.keys(qtyByName);
        let newQty = 0;
        const groupProducts = [];
        if (qtyNames.length > 0) {
            const groupCount = Math.max(...qtyNames.map(k => qtyByName[k].length));
            for (let g = 0; g < groupCount; g++) {
                let groupProduct = 1;
                let groupHasBlankFactor = false;
                qtyNames.forEach(key => {
                    const values = qtyByName[key];
                    // Broadcast a single-occurrence characteristic's value across
                    // every group; otherwise use that group's own occurrence.
                    const raw = values.length > g ? values[g] : values[0];
                    const val = parseFloat(raw) || 0;
                    if (!val) groupHasBlankFactor = true;
                    groupProduct *= val;
                });
                // If a factor within THIS group is 0/blank, only that group's
                // contribution is voided — it no longer zeroes out the whole
                // total the way one flat product used to.
                groupProducts.push(groupHasBlankFactor ? 0 : groupProduct);
            }
            newQty = groupProducts.reduce((sum, p) => sum + p, 0);
        }

        console.log(
            "qty groups:", qtyNames.map(k => `${k}=[${qtyByName[k].join(", ")}]`),
            "group products:", groupProducts,
            "qty:", newQty
        );

        // ---- Over/Under Tolerance Range ----
        // Checked (checkbox applied): Range base is the Length of the Drum/Coil
        // (not total Qty), tolerance % applied against Qty. This logic is
        // unchanged / already correct.
        // Range = length - (qty * underTolerance%) to length + (qty * overTolerance%)
        //
        // Unchecked: still calculate a full min-max range (don't leave Range
        // blank), but using (No. of Drum/Coil * Length of the Drum/Coil) as
        // the base instead of Length alone.
        // Range = (noOfDrum * length) - (qty * underTolerance%) to (noOfDrum * length) + (qty * overTolerance%)
        // Each "+ Add" click appends another Drum/Coil GROUP (row) with its
        // own Length / No. of Drum / Over / Under Tolerance values — so any
        // of these can appear more than once in newChars. A plain .find()
        // only ever grabs the FIRST group's values, which is why the Range
        // shown on the outer Enquiry table used to freeze on row 1 even
        // though Qty (above) is already correctly summed across every row.
        // Fix: group each of these four characteristics by occurrence
        // (same "nth occurrence = row g" pairing the modal's Range column
        // already uses), compute a min/max range per row, then combine
        // across rows — overall min = lowest of all rows' mins, overall
        // max = highest of all rows' maxes.
        const byName = (matcher) => newChars.filter(c => matcher((c.characteristic || '').trim().toLowerCase()));
        const overEntries = byName(n => n === 'over tolerance');
        const underEntries = byName(n => n === 'under tolerance');
        const lengthEntries = byName(n => n.includes('length') && (n.includes('drum') || n.includes('coil')));
        const noOfDrumEntries = byName(n => /no\.?\s*of\s*(drum|coil)/.test(n));

        const rowCount = Math.max(
            1,
            overEntries.length, underEntries.length, lengthEntries.length, noOfDrumEntries.length
        );

        let overallMin = null;
        let overallMax = null;
        let validGroupCount = 0;
        for (let g = 0; g < rowCount; g++) {
            // Single-occurrence characteristics broadcast to every row (same
            // fallback rule used for qty above); repeated ones use their own
            // row's entry.
            const overEntry = overEntries.length > g ? overEntries[g] : overEntries[0];
            const underEntry = underEntries.length > g ? underEntries[g] : underEntries[0];
            const lengthEntry = lengthEntries.length > g ? lengthEntries[g] : lengthEntries[0];
            const noOfDrumEntry = noOfDrumEntries.length > g ? noOfDrumEntries[g] : noOfDrumEntries[0];

            const rowToleranceApplied = !!(overEntry?.tolerance_applied || underEntry?.tolerance_applied);
            const rowLengthVal = parseFloat(lengthEntry?.value) || 0;
            const rowNoOfDrumVal = parseFloat(noOfDrumEntry?.value) || 0;
            const rowOverPct = parseFloat(overEntry?.value) || 0;
            const rowUnderPct = parseFloat(underEntry?.value) || 0;
            // This row's own qty contribution — same base handleSaveCharModal
            // uses per group when summing newQty above.
            const rowQty = rowNoOfDrumVal * rowLengthVal;
            if (!rowLengthVal && !rowQty) continue;

            // Shared formula (see calculateToleranceRange in ProductConfiguration.js
            // for the checked/unchecked logic).
            const { rangeMin, rangeMax } = calculateToleranceRange({
                toleranceApplied: rowToleranceApplied,
                length: rowLengthVal,
                noOfDrum: rowNoOfDrumVal,
                qty: rowQty,
                overPercent: rowOverPct,
                underPercent: rowUnderPct
            });
            if (rangeMin === null || rangeMax === null) continue;

            validGroupCount++;
            if (overallMin === null || rangeMin < overallMin) overallMin = rangeMin;
            if (overallMax === null || rangeMax > overallMax) overallMax = rangeMax;
        }

        // More than one Drum/Coil group ("+ Add"-ed) on this item — a single
        // combined min-max Range here would misleadingly read as one
        // continuous span (e.g. "97 - 208") when the groups actually have
        // their own separate ranges (97-103, 192-208). Point to the Fast
        // Entry (▶) table instead, which already lists each group's Range.
        const rangeText = validGroupCount > 1
            ? "Refer Table"
            : (overallMin !== null ? `${overallMin} - ${overallMax}` : '');

        const updated = [...items];
        updated[modalIndex].characteristics = newChars;
        updated[modalIndex].range = collapseIfSameRange(rangeText);
        if (newQty > 0) {
            updated[modalIndex].quantity = newQty;
            const price = parseFloat(updated[modalIndex].unit_price) || 0;
            updated[modalIndex].total_price = (newQty * price).toFixed(2);
        }
        setItems(updated);
        setModalVisible(false);
    };

    // Qty must be greater than 0 for every line item — a blank or 0 quantity
    // is not a valid enquiry line, so block Create/Save/Submit until it's fixed.
    const validateQtyFilled = (rows) => {
        const missingRows = rows
            .map((r, i) => ({ ok: parseFloat(r.quantity) > 0, item: i + 1 }))
            .filter(r => !r.ok);
        if (missingRows.length > 0) {
            toast.error(
                missingRows.length === rows.length
                    ? "Qty must be greater than 0 for all line items."
                    : `Qty must be greater than 0. Missing/zero for item(s): ${missingRows.map(r => r.item).join(", ")}.`
            );
            return false;
        }
        return true;
    };

    // Req. Date is mandatory on every product line item — enquiry, quotation
    // and sales order all enforce this the same way, before any save/create/
    // submit call reaches the backend.
    const validateReqDatesFilled = (rows) => {
        const missingRows = rows
            .map((r, i) => ({ ok: !!r.req_date, item: i + 1 }))
            .filter(r => !r.ok);
        if (missingRows.length > 0) {
            toast.error(
                missingRows.length === rows.length
                    ? "Req. Date is mandatory for all line items."
                    : `Req. Date is mandatory. Missing for item(s): ${missingRows.map(r => r.item).join(", ")}.`
            );
            return false;
        }
        return true;
    };

    // Unit Price must be greater than 0 for every line item — same
    // enforcement pattern as Qty/Req. Date, blocking Create/Save/Submit
    // until every row has a real price.
    const validateUnitPriceFilled = (rows) => {
        const missingRows = rows
            .map((r, i) => ({ ok: parseFloat(r.unit_price) > 0, item: i + 1 }))
            .filter(r => !r.ok);
        if (missingRows.length > 0) {
            toast.error(
                missingRows.length === rows.length
                    ? "Unit Price must be greater than 0 for all line items."
                    : `Unit Price must be greater than 0. Missing/zero for item(s): ${missingRows.map(r => r.item).join(", ")}.`
            );
            return false;
        }
        return true;
    };

    const handleCreateInquiry = async () => {
        if (!enquiryType || !salesOrg || !customerSegment || !dChannel) {
            toast.error("Please fill all Header fields (Type, Org, Segment, Channel).");
            return;
        }
        if (!selectedCustomer) { toast.error("Please select a Customer."); return; }

        if (items.length === 0) { toast.error("Please add at least one line item."); return; }

        if (!validateQtyFilled(items)) return;
        if (!validateReqDatesFilled(items)) return;
        if (!validateUnitPriceFilled(items)) return;

        // Enquiry ID is generated server-side now (prefix configured under
        // Configuration → ID Creation, module "Enquiry") — no id is sent
        // on this first save, and we read the real one back below.
        if (isExportEnquiry && !String(customerCountry || "").trim()) {
            toast.error("Customer Country is mandatory for an Export enquiry!");
            return;
        }

        const payload = {
            enquiryType, salesOrg, customerSegment, dChannel,
            customer: selectedCustomer?.label, submittedBy, comment: submitComment,
            status: "Draft", items, project,
            // 🆕 Export enquiry — sent as null for a Domestic enquiry so nothing
            // stale carries over on the backend.
            customerCountry: isExportEnquiry ? (customerCountry || null) : null,
            exchangeRate: isExportEnquiry ? (enqExchangeRate || null) : null
        };

        try {
            const res = await fetch(`${ORDER_API}/enquiry/save`,{
                method: "POST", headers: { ...authHeaders(), "Content-Type": "application/json" },
                body: JSON.stringify(payload)
            });

            if (res.ok) {
                const result = await res.json();
                const generatedId = result?.enquiryId;

                if (!generatedId) {
                    toast.error("Server did not return an Enquiry ID.");
                    return;
                }

                setEnquiryId(generatedId);
                toast.success(`Enquiry ${generatedId} Created`, { transition: Zoom });
                fetchSummary();
            } else toast.error("Failed to save Draft");
        } catch (err) { toast.error("Server Error"); }
    };
    const handleUpdateInquiry = async () => {
        if (!validateQtyFilled(items)) return;
        if (!validateReqDatesFilled(items)) return;
        if (!validateUnitPriceFilled(items)) return;

        if (isExportEnquiry && !String(customerCountry || "").trim()) {
            toast.error("Customer Country is mandatory for an Export enquiry!");
            return;
        }

        const payload = {
            enquiryType: enquiryType,
            salesOrg: salesOrg,
            customerSegment: customerSegment,
            dChannel: dChannel,
            customer: selectedCustomer?.label,
            submittedBy: submittedBy,
            comment: submitComment,
            items: items,
            project: project,
            // 🆕 Export enquiry
            customerCountry: isExportEnquiry ? (customerCountry || null) : null,
            exchangeRate: isExportEnquiry ? (enqExchangeRate || null) : null
        };

        try {

            const res = await fetch(
                `${ORDER_API}/enquiry/update/${enquiryId}`,{
                    method: "PUT",
                    headers: { ...authHeaders(), "Content-Type": "application/json" },
                    body: JSON.stringify(payload)
                });

            if (res.ok) {
                toast.success("Enquiry Updated Successfully");
                fetchSummary();

                // Re-fetch the saved rows from DB so React state matches DB exactly.
                // This prevents a second Update from re-logging unchanged fields
                // (because the diff compares DB values against stale in-memory values).
                const [freshRows, logRes] = await Promise.all([
                    fetch(`${ORDER_API}/enquiry/${enquiryId}`, { headers: authHeaders() }).then(r => r.json()),
                    fetch(`${ORDER_API}/enquiry/change-logs/${enquiryId}`, { headers: authHeaders() })
                ]);
                if (Array.isArray(freshRows) && freshRows.length > 0) {
                    setItems(freshRows.map(r => ({
                        id: r.id,
                        product_code: r.product_code,
                        description: r.description,
                        uom: r.uom,
                        quantity: r.quantity,
                        unit_price: r.unit_price,
                        req_date: r.requirement_date
                            ? (() => {
                                const s = String(r.requirement_date);
                                // Already a plain date string like "2026-06-18"
                                if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
                                // ISO string like "2026-06-18T00:00:00.000Z" — extract date part only
                                return s.substring(0, 10);
                            })()
                            : "",
                        total_price: r.total_price,
                        product_class: r.product_class,
                        currency: r.currency || "",
                        range: collapseIfSameRange(r.range_qty) || "",
                        characteristics: normalizeCharacteristics(r.characteristics)
                    })));
                    // 🆕 Keep Export enquiry fields in sync with what was just saved
                    const h = freshRows[0];
                    setCustomerCountry(h.customer_country || "");
                    setEnqExchangeRate(h.exchange_rate || "");
                }
                if (logRes.ok) setEnquiryLogs(await logRes.json());
                setModalMode("view");
                setActiveTab("product");

            } else {

                toast.error("Failed to update Enquiry");
            }

        } catch (error) {

            toast.error("Server Error");
        }
    };

    // Approve/Reject from the Review tab — calls the existing
    // PUT /enquiry/approve/:id and /enquiry/reject/:id routes, backed by
    // enquiry_service.setEnquiryStatus. Only valid from status "Submitted"
    // (enforced again server-side).
    const handleReviewDecision = async (action) => {
        if (!enquiryId) return;
        try {
            setReviewSubmitting(true);
            const endpoint = action === 'approve' ? 'approve' : 'reject';
            const res = await fetch(`${ORDER_API}/enquiry/${endpoint}/${enquiryId}`,{
                method: "PUT",
                headers: { ...authHeaders(), "Content-Type": "application/json" },
                body: JSON.stringify({ submittedBy: submittedBy, comment: reviewComment })
            });

            const data = await res.json().catch(() => ({}));

            if (res.ok && data.success) {
                toast.success(action === 'approve' ? "Enquiry Approved" : "Enquiry Rejected");
                setEnquiryStatus(data.data?.status || (action === 'approve' ? 'Approved' : 'Rejected'));
                setReviewComment("");
                const logRes = await fetch(`${ORDER_API}/enquiry/change-logs/${enquiryId}`, { headers: authHeaders() });
                if (logRes.ok) setEnquiryLogs(await logRes.json());
                fetchSummary();
            } else {
                toast.error(data.message || "Failed to submit decision");
            }
        } catch (err) {
            toast.error("Server Error");
        } finally {
            setReviewSubmitting(false);
        }
    };

    const handleFinalSubmit = async () => {
        if (!validateQtyFilled(items)) return;
        if (!validateReqDatesFilled(items)) return;
        if (!validateUnitPriceFilled(items)) return;
        if (!submitComment.trim()) {
            toast.error("Comment is required to submit.", { transition: Zoom });
            return;
        }

        const payload = {
            enquiryId, enquiryType, salesOrg, customerSegment, dChannel,
            customer: selectedCustomer?.label, submittedBy, comment: submitComment,
            status: "Submitted", items, project,
            // 🆕 Export enquiry — was missing here, which is why customer_country /
            // exchange_rate saved NULL whenever the record went through Submit
            // instead of Create/Update.
            customerCountry: isExportEnquiry ? (customerCountry || null) : null,
            exchangeRate: isExportEnquiry ? (enqExchangeRate || null) : null
        };

        try {
            const res = await fetch(`${ORDER_API}/enquiry/save`,{
                method: "POST", headers: { ...authHeaders(), "Content-Type": "application/json" },
                body: JSON.stringify(payload)
            });

            if (res.ok) {
                toast.success("Enquiry Submitted Successfully!", { transition: Zoom });
                setShowCommentModal(false); setShowModal(false); fetchSummary();
            } else toast.error("Failed to submit");
        } catch (err) { toast.error("Server Error"); }
    };

    const filteredData = useMemo(() => {
        return summaryData.filter(item => {
            return (
                (filterSalesOrg === "" || item.sales_organization === filterSalesOrg) &&
                (filterCustomerSegment === "" || item.customer_segment === filterCustomerSegment) &&
                (filterDChannel === "" || item.distribution_channel === filterDChannel) &&
                (filterId === "" || item.enquiry_id === filterId) &&
                (filterCustomer === null || item.customer.toLowerCase().includes(filterCustomer.label.toLowerCase()))
            );
        });
    }, [summaryData, filterSalesOrg, filterCustomerSegment, filterDChannel, filterId, filterCustomer]);

    const limit = 10;
    const totalPages = Math.max(1, Math.ceil(filteredData.length / limit));
    const paginatedData = useMemo(() => {
        const start = (page - 1) * limit;
        return filteredData.slice(start, start + limit);
    }, [filteredData, page]);

    useEffect(() => { setPage(1); }, [filterSalesOrg, filterCustomerSegment, filterDChannel, filterId, filterCustomer]);

    const formatCurrency = (value) => {
        const val = Number(value);
        if (isNaN(val)) return "0.00";
        return val.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    // Safely format any date (Date object, "YYYY-MM-DD", or ISO timestamp string)
    // as "DD-MM-YYYY" for display. Strings are never passed through `new Date()`
    // for the display path — we read the calendar digits straight off the string —
    // so IST/UTC conversion can never shift the day backward.
    const formatDateLocal = (dateInput) => {
        if (!dateInput) return "";
        let year, month, day;
        if (dateInput instanceof Date) {
            if (isNaN(dateInput.getTime())) return "";
            year = dateInput.getFullYear();
            month = String(dateInput.getMonth() + 1).padStart(2, '0');
            day = String(dateInput.getDate()).padStart(2, '0');
        } else {
            const clean = String(dateInput).substring(0, 10); // "YYYY-MM-DD"
            [year, month, day] = clean.split("-");
            if (!year || !month || !day) return "";
        }
        return `${day}-${month}-${year}`;
    };

    // Fixed dd/mm/yyyy hh:mm:ss formatter for the Change Log's "Date & Time"
    // column. `new Date(...).toLocaleString()` renders using the viewer's OS/
    // browser locale (e.g. mm/dd/yyyy on a US machine), so two users looking
    // at the same log row can see different formats/orderings. This builds
    // the string manually from the Date's own fields so it's always
    // "dd/mm/yyyy hh:mm:ss" regardless of the user's system settings.
    const formatChangeLogDateTime = (dateInput) => {
        if (!dateInput) return "-";
        const d = dateInput instanceof Date ? dateInput : new Date(dateInput);
        if (isNaN(d.getTime())) return "-";
        // Always render in Indian Standard Time (Asia/Kolkata), regardless of
        // the viewer's device timezone or the UTC value sent by the server.
        const parts = new Intl.DateTimeFormat('en-GB', {
            timeZone: 'Asia/Kolkata',
            day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit', second: '2-digit',
            hour12: false
        }).formatToParts(d).reduce((acc, p) => { acc[p.type] = p.value; return acc; }, {});
        const hh = parts.hour === '24' ? '00' : parts.hour;
        return `${parts.day}/${parts.month}/${parts.year} ${hh}:${parts.minute}:${parts.second}`;
    };

    const clearGlobalFilters = () => {
        setGlobalFilters({
            construction: [], code: [], cableType: [], category: [],
            noOfCores: [], color: [], nominalCrossSection: [], referenceOfStandard: [], voltage: []
        });
        setFilteredProductList(null);
    };
    // ----------------------------


    return (
        <Layout>
            <style>{`
                .no-spinner::-webkit-outer-spin-button,
                .no-spinner::-webkit-inner-spin-button {
                    -webkit-appearance: none;
                    margin: 0;
                }
                .no-spinner {
                    -moz-appearance: textfield;
                }
            `}</style>
                        <div style={styles.pageTitleBar}>
                <div>
                    <div style={styles.pageTitle}>Enquiry Management</div>
                    <div style={styles.pageSubtitle}>Manage customer Enquiries and Quotations</div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    {canCreate && (
                        <button className="btn btn-dark" style={styles.primaryBtn} onClick={handleOpenCreate}>
                            + Create Enquiry
                        </button>
                    )}
                    {/* --- User Manual Icon Button --- */}
                      <button
    onClick={() => {
        setManualExists(null);
        setManualFile(null);
        setShowManual(true);
        fetch(USER_MANUAL_LIST_API, { headers: authHeaders() })
            .then((res) => res.json())
            .then((data) => {
                const norm = (v) => String(v || "").trim().toLowerCase();
                const match = (data?.data || []).find((m) =>
                    norm(m.module_name) === norm(USER_MANUAL_SCOPE.moduleName) &&
                    norm(m.process_name) === norm(USER_MANUAL_SCOPE.process) &&
                    norm(m.sub_process_name) === norm(USER_MANUAL_SCOPE.subProcess)
                );
                if (match && match.drive_link) {
                    setManualFile(match);
                    setManualExists(true);
                } else {
                    setManualExists(false);
                }
            })
            .catch(() => setManualExists(false));
    }}
                        title="View User Manual"
                        style={{
                            background: "#ffffff",
                            border: "1px solid #e2e8f0",
                            borderRadius: "10px",
                            padding: "10px 14px",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            cursor: "pointer",
                            color: "#3b82f6",
                            fontSize: "14px",
                            fontWeight: "600",
                            boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#3b82f6"; }}
                        onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e2e8f0"; }}
                    >
                        <FileText size={18} />
                        User Manual
                    </button>
                </div>
            </div>

            {/* --- User Manual Popup Modal --- */}
            {showManual && (
                <div
                    onClick={() => setShowManual(false)}
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100vw",
                        height: "100vh",
                        backgroundColor: "rgba(15, 23, 42, 0.6)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 1100,
                    }}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            background: "#ffffff",
                            borderRadius: "12px",
                            width: "90vw",
                            height: "97vh",
                            display: "flex",
                            flexDirection: "column",
                            overflow: "hidden",
                            boxShadow: "0 20px 25px -5px rgba(0,0,0,0.2)",
                        }}
                    >
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                padding: "14px 20px",
                                borderBottom: "1px solid #e2e8f0",
                            }}
                        >
                            <h4 style={{ margin: 0, color: "#0f172a", fontSize: "16px", fontWeight: "600" }}>
                                User Manual
                            </h4>
                            <button
                                onClick={() => setShowManual(false)}
                                style={{
                                    border: "none",
                                    background: "#f1f5f9",
                                    borderRadius: "8px",
                                    width: "32px",
                                    height: "32px",
                                    cursor: "pointer",
                                    fontSize: "18px",
                                    color: "#475569",
                                    lineHeight: 1,
                                }}
                                title="Close"
                            >
                                ×
                            </button>
                        </div>

                                               {manualExists === null && (
                            <div style={{
                                flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
                                color: "#94a3b8", fontSize: "14px"
                            }}>
                                Checking for user manual...
                            </div>
                        )}

                        {manualExists === false && (
                            <div style={{
                                flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                                color: "#64748b", gap: "10px", padding: "20px", textAlign: "center"
                            }}>
                                <FileText size={40} color="#cbd5e1" />
                                <div style={{ fontSize: "15px", fontWeight: 600, color: "#334155" }}>
                                    User Manual Not Available
                                </div>
                                <div style={{ fontSize: "13px", color: "#94a3b8", maxWidth: "320px" }}>
                                    The user manual for this module hasn't been uploaded yet. Please check back later or contact your administrator.
                                </div>
                            </div>
                        )}

                        {manualExists === true && manualFile && (() => {
                            const MANUAL_SCALE = 1.25; // 1.0 = normal, 1.25 = 25% bigger, 1.5 = 50% bigger
                            return (
                                <div style={{ flex: 1, overflow: "hidden", position: "relative" }}>
                                    <iframe
                                        src={manualFile.drive_link.replace("/view", "/preview")}
                                        title={manualFile.file_name || "User Manual"}
                                        allow="autoplay"
                                        style={{
                                            border: "none",
                                            width: `${100 / MANUAL_SCALE}%`,
                                            height: `${100 / MANUAL_SCALE}%`,
                                            transform: `scale(${MANUAL_SCALE})`,
                                            transformOrigin: "top left",
                                        }}
                                    />
                                </div>
                            );
                        })()}
                    </div>
                </div>
            )}

            {/* --- FILTER CARD + LIST: hidden for CREATE-only (add-only) roles,
                 which have can_view = 0 and no visibility into existing records --- */}
            {canView && (
            <>
            <div style={styles.filterCard}>
                <div className="row g-4">
                    <div className="col-md-2">
                        <label style={styles.filterLabel}>Sales Organization</label>
                        <Select
                            options={salesOrgList.map(opt => ({ value: opt.code || opt.sales_organization, label: opt.code || opt.sales_organization }))}
                            value={filterSalesOrg ? { value: filterSalesOrg, label: filterSalesOrg } : null}
                            onChange={opt => setFilterSalesOrg(opt ? opt.value : "")}
                            placeholder="Select"
                            isClearable
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            styles={{
                                ...dropdownOptionStyle,
                                menuPortal: (base) => ({ ...base, zIndex: 9999 })
                            }}
                        />
                    </div>
                    <div className="col-md-2">
                        <label style={styles.filterLabel}>Customer Segment</label>
                        <Select
                            options={customerSegmentList.map(opt => ({ value: opt.name || opt.customer_segment, label: opt.name || opt.customer_segment }))}
                            value={filterCustomerSegment ? { value: filterCustomerSegment, label: filterCustomerSegment } : null}
                            onChange={opt => setFilterCustomerSegment(opt ? opt.value : "")}
                            placeholder="Select"
                            isClearable
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            styles={{
                                ...dropdownOptionStyle,
                                menuPortal: (base) => ({ ...base, zIndex: 9999 })
                            }}
                        />
                    </div>
                    <div className="col-md-2">
                        <label style={styles.filterLabel}>Distribution Channel</label>
                        <Select
                            options={channelList.map(opt => ({ value: opt.name || opt.distribution_channel, label: opt.name || opt.distribution_channel }))}
                            value={filterDChannel ? { value: filterDChannel, label: filterDChannel } : null}
                            onChange={opt => setFilterDChannel(opt ? opt.value : "")}
                            placeholder="Select"
                            isClearable
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            styles={{
                                ...dropdownOptionStyle,
                                menuPortal: (base) => ({ ...base, zIndex: 9999 })
                            }}
                        />
                    </div>
                    <div className="col-md-2">
                        <label style={styles.filterLabel}>Enquiry ID</label>
                        <Select
                            options={summaryData.map(row => ({ value: row.enquiry_id, label: row.enquiry_id }))}
                            value={filterId ? { value: filterId, label: filterId } : null}
                            onChange={opt => setFilterId(opt ? opt.value : "")}
                            placeholder="Type ID..."
                            isClearable
                            isSearchable
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            styles={{
                                ...dropdownOptionStyle,
                                menuPortal: (base) => ({ ...base, zIndex: 9999 })
                            }}
                        />
                    </div>
                    <div className="col-md-4">
                        <label style={styles.filterLabel}>Customer</label>
                        <Select
                            options={allCustomerOptions}
                            value={filterCustomer}
                            onChange={setFilterCustomer}
                            placeholder="Select Customer"
                            isClearable
                            isSearchable
                            filterOption={(option, inputValue) => {
                                if (!inputValue) return true;
                                return option.label.toLowerCase().includes(inputValue.toLowerCase());
                            }}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            styles={{
                                ...dropdownOptionStyle,
                                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                menuList: (base) => ({ ...base, padding: 4, maxHeight: 240, overflowY: 'auto' })
                            }}
                        />
                    </div>
                </div>
            </div>

            {/* --- MAIN TABLE --- */}
            <div style={styles.tableContainer}>
                {loading && <div className="p-3 text-muted">Loading...</div>}
                <table className="table" style={styles.erpTable}>
                    <thead>
                        <tr>
                            <th style={styles.erpTh}>Enquiry ID</th>
                            <th style={styles.erpTh}>Customer</th>
                            <th style={styles.erpTh}>Type of Enquiry</th>
                            <th style={styles.erpTh}>Sales Org</th>
                            <th style={styles.erpTh}>Customer Segment</th>
                            <th style={styles.erpTh}>Distribution Channel</th>
                            <th style={styles.erpTh}>Product Count</th>
                            <th style={styles.erpTh}>Creation Date</th>
                            <th style={styles.erpTh}>Status</th>
                            <th style={styles.erpTh}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedData.length > 0 ? paginatedData.map(row => (
                            <tr key={row.enquiry_id}>
                                <td style={styles.erpTd}>
                                    <span
                                        onClick={() => handleOpenView(row.enquiry_id)}
                                        style={{ color: '#2563eb', cursor: 'pointer', fontWeight: 600, textDecoration: 'underline', textUnderlineOffset: '2px' }}
                                    >
                                        {row.enquiry_id}
                                    </span>
                                </td>
                                <td style={styles.erpTd}>{row.customer}</td>
                                <td style={styles.erpTd}>{row.enquiry_type || '—'}</td>
                                <td style={styles.erpTd}>{row.sales_organization}</td>
                                <td style={styles.erpTd}>{row.customer_segment || '—'}</td>
                                <td style={styles.erpTd}>{row.distribution_channel || '—'}</td>
                                <td style={styles.erpTd}>{row.product_count ?? '—'}</td>
                                {/* UPDATE THIS LINE inside your <tbody> loop */}
                                <td style={styles.erpTd}>{row.created_at ? formatDateLocal(row.created_at) : '—'}</td>
                                <td style={styles.erpTd}>{row.status}</td>
                                <td style={{ ...styles.erpTd, whiteSpace: 'nowrap' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <button className="btn btn-outline-dark btn-sm" style={{ fontSize: 11 }}
                                            onClick={() => handleOpenView(row.enquiry_id)}>View</button>
                                        {canCreate && (
                                        <button
                                            title="Delete"
                                            onClick={() => handleDeleteEnquiry(row.enquiry_id)}
                                            style={{
                                                background: "#fff",
                                                border: "1px solid #f5c2c7",
                                                borderRadius: "4px",
                                                display: "inline-flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                padding: "4px 8px",
                                                cursor: "pointer"
                                            }}
                                        >
                                            <svg width="14" height="14" viewBox="0 0 16 16" fill="#dc3545" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5Zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5Zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6Z" />
                                                <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1ZM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118ZM2.5 3V2h11v1h-11Z" />
                                            </svg>
                                        </button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        )) : (
                            !loading && <tr><td colSpan="10" className="text-center p-3 text-muted">No Records Found</td></tr>
                        )}
                    </tbody>
                </table>
                <div className="d-flex align-items-center justify-content-between pt-3">
                    <div style={{ fontSize: 13, color: "#64748b" }}>Page <b>{page}</b> of <b>{totalPages}</b></div>
                    <div className="d-flex gap-2">
                        <button className="btn btn-outline-secondary btn-sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</button>
                        <button className="btn btn-outline-secondary btn-sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</button>
                    </div>
                </div>
            </div>
            </>
            )}

            {/* --- MAIN MODAL --- */}
            {showModal && (
                <div style={styles.modalBackdrop}>
                    <div style={{ ...styles.modal, maxWidth: "1250px", width: "95%", maxHeight: "90vh" }}>
                        <div style={styles.modalHeader}>
                            <div style={{ fontWeight: 600, fontSize: '18px', color: '#1e293b' }}>
                                {modalMode === 'create' ? "Create  Enquiry" : `Enquiry: ${enquiryId}`}
                            </div>
                            <button style={styles.iconBtn} onClick={handleCloseModal}>✕</button>
                        </div>

                        <div style={styles.modalBody}>

                            {/* Reference Enquiry — create mode only: pick an existing enquiry to copy from */}
                            {modalMode === 'create' && (
                                <div className="row g-3 mb-3">
                                    <div className="col-md-6">
                                        <label style={{ ...styles.compactLabel, display: 'block' }}>Reference Enquiry</label>
                                        <Select
                                            options={summaryData.map(row => ({ value: row.enquiry_id, label: row.enquiry_id }))}
                                            value={selectedRefEnquiry}
                                            onChange={handleRefEnquirySelect}
                                            isClearable
                                            placeholder="Select an existing Enquiry to copy from..."
                                            menuPortalTarget={document.body}
                                            menuPosition="fixed"
                                            styles={{
                                                ...dropdownOptionStyle,
                                                control: (b) => ({ ...b, minHeight: 34, fontSize: '13px', borderColor: '#6366f1', boxShadow: selectedRefEnquiry ? '0 0 0 2px rgba(99,102,241,0.2)' : 'none' }),
                                                menuPortal: (b) => ({ ...b, zIndex: 99999 })
                                            }}
                                        />
                                    </div>
                                </div>
                            )}

                            <div className="row g-3 mb-3">
                                <div className="col-md-3">
                                    <label style={styles.compactLabel}>Type of enquiry <span style={{ color: 'red' }}>*</span></label>
                                    {modalMode === 'view' && !isFieldVisible('enquiry_type') ? (
                                        <MaskedValue />
                                    ) : (
                                    <Select
                                        options={enquiryTypeList.map(opt => ({ value: opt.name || opt.enquiry_type, label: opt.name || opt.enquiry_type }))}
                                        value={enquiryType ? { value: enquiryType, label: enquiryType } : null}
                                        onChange={opt => {
                                            const val = opt ? opt.value : "";
                                            setEnquiryType(val);
                                            // Clear Export-only fields when switching away from Export
                                            if (String(val).trim().toLowerCase() !== "export" && customerCountry) {
                                                setCustomerCountry("");
                                            }
                                        }}
                                        isDisabled={modalMode === 'view' || isFieldLocked('enquiry_type')}
                                        placeholder="Select"
                                        menuPortalTarget={document.body}
                                        menuPosition="fixed"
                                        styles={{
                                            ...dropdownOptionStyle,
                                            control: (base) => ({ ...base, minHeight: 34, fontSize: '13px' }),
                                            menuPortal: (base) => ({ ...base, zIndex: 99999 })
                                        }}
                                    />
                                    )}
                                </div>
                                <div className="col-md-3">
                                    <label style={styles.compactLabel}>Sales Organization <span style={{ color: 'red' }}>*</span></label>
                                    {modalMode === 'view' && !isFieldVisible('sales_org') ? (
                                        <MaskedValue />
                                    ) : (
                                    <Select
                                        options={salesOrgList.map(opt => ({ value: opt.code || opt.sales_organization, label: opt.code || opt.sales_organization }))}
                                        value={salesOrg ? { value: salesOrg, label: salesOrg } : null}
                                        onChange={opt => setSalesOrg(opt ? opt.value : "")}
                                        isDisabled={modalMode === 'view' || isFieldLocked('sales_org')}
                                        placeholder="Select"
                                        menuPortalTarget={document.body}
                                        menuPosition="fixed"
                                        styles={{
                                            ...dropdownOptionStyle,
                                            control: (base) => ({ ...base, minHeight: 34, fontSize: '13px' }),
                                            menuPortal: (base) => ({ ...base, zIndex: 99999 })
                                        }}
                                    />
                                    )}
                                </div>
                                <div className="col-md-3">
                                    <label style={styles.compactLabel}>Customer Segment <span style={{ color: 'red' }}>*</span></label>
                                    {modalMode === 'view' && !isFieldVisible('customer_segment') ? (
                                        <MaskedValue />
                                    ) : (
                                    <Select
                                        options={customerSegmentList.map(opt => ({ value: opt.name || opt.customer_segment, label: opt.name || opt.customer_segment }))}
                                        value={customerSegment ? { value: customerSegment, label: customerSegment } : null}
                                        onChange={opt => setCustomerSegment(opt ? opt.value : "")}
                                        isDisabled={modalMode === 'view' || isFieldLocked('customer_segment')}
                                        placeholder="Select"
                                        menuPortalTarget={document.body}
                                        menuPosition="fixed"
                                        styles={{
                                            ...dropdownOptionStyle,
                                            control: (base) => ({ ...base, minHeight: 34, fontSize: '13px' }),
                                            menuPortal: (base) => ({ ...base, zIndex: 99999 })
                                        }}
                                    />
                                    )}
                                </div>
                                <div className="col-md-3">
                                    <label style={styles.compactLabel}>Distribution Channel <span style={{ color: 'red' }}>*</span></label>
                                    {modalMode === 'view' && !isFieldVisible('distribution_channel') ? (
                                        <MaskedValue />
                                    ) : (
                                    <Select
                                        options={channelList.map(opt => ({ value: opt.name || opt.distribution_channel, label: opt.name || opt.distribution_channel }))}
                                        value={dChannel ? { value: dChannel, label: dChannel } : null}
                                        onChange={opt => setDChannel(opt ? opt.value : "")}
                                        isDisabled={modalMode === 'view' || isFieldLocked('distribution_channel')}
                                        placeholder="Select"
                                        menuPortalTarget={document.body}
                                        menuPosition="fixed"
                                        styles={{
                                            ...dropdownOptionStyle,
                                            control: (base) => ({ ...base, minHeight: 34, fontSize: '13px' }),
                                            menuPortal: (base) => ({ ...base, zIndex: 99999 })
                                        }}
                                    />
                                    )}
                                </div>
                            </div>

                            {/* ---- ROW 2: Created By / Created Date / Approved By ---- */}
                            <div className="row g-3 mb-3">
                                <div className="col-md-3">
                                    <label style={styles.compactLabel}>Created By</label>
                                    {modalMode === 'view' && !isFieldVisible('created_by') ? (
                                        <MaskedValue />
                                    ) : (
                                    <input
                                        type="text"
                                        className="form-control"
                                        style={{ ...styles.compactInput, background: '#f8fafc', color: '#374151', cursor: 'default' }}
                                        value={createdBy}
                                        readOnly
                                    />
                                    )}
                                </div>
                                <div className="col-md-3">
                                    <label style={styles.compactLabel}>Created Date</label>
                                    {modalMode === 'view' && !isFieldVisible('created_date') ? (
                                        <MaskedValue />
                                    ) : (
                                    <input
                                        type="text"
                                        className="form-control"
                                        style={{ ...styles.compactInput, background: '#f8fafc', color: '#374151', cursor: 'default' }}
                                        value={createdDate}
                                        readOnly
                                    />
                                    )}
                                </div>
                                <div className="col-md-3">
                                    <label style={styles.compactLabel}>Project</label>
                                    {modalMode === 'view' && !isFieldVisible('project') ? (
                                        <MaskedValue />
                                    ) : (
                                    <input
                                        type="text"
                                        className="form-control"
                                        style={styles.compactInput}
                                        value={project}
                                        onChange={(e) => setProject(e.target.value)}
                                        disabled={modalMode === 'view' || isFieldLocked('project')}
                                        placeholder="Enter Project"
                                    />
                                    )}
                                </div>

                            </div>

                            <div className="p-3 bg-light rounded border mb-3">
                                <div className="row g-3 align-items-start">
                                    {/* --- CREATABLE CUSTOMER SELECT --- */}
                                    <div className="col-md-6">
                                        <label style={{ ...styles.compactLabel, display: 'block', marginBottom: '4px' }}>Customer <span style={{ color: 'red' }}>*</span></label>
                                        {modalMode === 'view' && !isFieldVisible('customer') ? (
                                            <MaskedValue height={38} />
                                        ) : (
                                        <AsyncCreatableSelect
                                            cacheOptions loadOptions={loadCustomerOptions} defaultOptions
                                            value={selectedCustomer} isDisabled={modalMode === 'view' || isFieldLocked('customer')}
                                            onChange={setSelectedCustomer}
                                            placeholder="Select or Type Customer..."
                                            formatCreateLabel={(val) => `Use: "${val}"`}
                                            menuPortalTarget={document.body}
                                            menuPosition="fixed"
                                            styles={{
                                                ...dropdownOptionStyle,
                                                control: (p) => ({ ...p, minHeight: 38, fontSize: '14px' }),
                                                menuPortal: (base) => ({ ...base, zIndex: 99999 })
                                            }}
                                        />
                                        )}
                                    </div>

                                    {/* 🆕 Customer Country — only shown when Enquiry Type is "Export";
                                        sits right after Customer (not up in the header row). Drives the
                                        auto-fetched Currency/Exchange Rate used to compute product Unit
                                        Price/Currency in the Product tab below. */}
                                    {isExportEnquiry && (
                                        <div className="col-md-6">
                                            <label style={{ ...styles.compactLabel, display: 'block', marginBottom: '4px' }}>Customer Country <span style={{ color: 'red' }}>*</span></label>
                                            {modalMode === 'view' && !isFieldVisible('customer_country') ? (
                                                <MaskedValue height={38} />
                                            ) : (
                                            <Select
                                                options={countryOptions.map(c => ({
                                                    value: c.name || c.country || c.value,
                                                    label: c.name || c.country || c.value
                                                }))}
                                                value={customerCountry ? { value: customerCountry, label: customerCountry } : null}
                                                onChange={opt => setCustomerCountry(opt ? opt.value : "")}
                                                isDisabled={modalMode === 'view' || isFieldLocked('customer_country')}
                                                placeholder="Select Country"
                                                menuPortalTarget={document.body}
                                                menuPosition="fixed"
                                                styles={{
                                                    ...dropdownOptionStyle,
                                                    control: (p) => ({ ...p, minHeight: 38, fontSize: '14px' }),
                                                    menuPortal: (base) => ({ ...base, zIndex: 99999 })
                                                }}
                                            />
                                            )}
                                        </div>
                                    )}

                                    {/* --- EXCEL UPLOAD FOR INQUIRY --- */}
                                    {modalMode === 'create' && (
                                        <div className="col-md-6">
                                            {/* <label style={{ ...styles.compactLabel, display: 'block', marginBottom: '4px' }}>Upload Excel</label> */}
                                            <div className="d-flex gap-2">
                                                {/* <button
                                                    className="btn btn-outline-dark btn-sm px-3"
                                                    style={{ height: 38, display: 'flex', alignItems: 'center', gap: 6 }}
                                                    onClick={() => { setSalesFile(null); setUploadMessage(""); setShowExcelModal(true); }}
                                                >
                                                    <span>📂</span> Choose File
                                                </button> */}
                                                {uploadMessage && <small className="text-success fw-bold align-self-center">{uploadMessage}</small>}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div style={styles.tabs}>
                                {isReviewOnly ? (
                                    // A Review-only role only ever gets the Review tab —
                                    // no Product/Docs/Change Log, per Role.js's field-config note.
                                    <button className={`btn ${activeTab === 'review' ? "btn-secondary" : "btn-light"}`}
                                        onClick={() => setActiveTab('review')} style={styles.compactTabBtn}> Review </button>
                                ) : (
                                    <>
                                        <button className={`btn ${activeTab === 'product' ? "btn-secondary" : "btn-light"}`}
                                            onClick={() => setActiveTab('product')} style={styles.compactTabBtn}> Product </button>
                                        <button className={`btn ${activeTab === 'docs' ? "btn-secondary" : "btn-light"}`}
                                            onClick={() => setActiveTab('docs')} style={styles.compactTabBtn}> Docs </button>
                                        {modalMode !== 'create' && (
                                            <button className={`btn ${activeTab === 'changeLog' ? "btn-secondary" : "btn-light"}`}
                                                onClick={() => setActiveTab('changeLog')} style={styles.compactTabBtn}> Change Log </button>
                                        )}
                                    </>
                                )}
                            </div>

                            {/* --- TAB: REVIEW (Approve/Reject a Submitted enquiry) --- */}
                            {activeTab === "review" && (
                                <div style={{ padding: '20px 4px' }}>
                                    <div style={{ marginBottom: 16 }}>
                                        <span style={{ fontSize: 13, color: '#64748b' }}>Current Status: </span>
                                        <span style={{
                                            fontSize: 13, fontWeight: 700,
                                            color: enquiryStatus === 'Approved' ? '#16a34a' : enquiryStatus === 'Rejected' ? '#ef4444' : '#334155'
                                        }}>
                                            {enquiryStatus || '—'}
                                        </span>
                                    </div>

                                    {enquiryStatus === 'Submitted' ? (
                                        <>
                                            <label style={styles.compactLabel}>Comment (optional)</label>
                                            <textarea
                                                className="form-control"
                                                rows={3}
                                                style={{ fontSize: 13, marginBottom: 16 }}
                                                value={reviewComment}
                                                onChange={e => setReviewComment(e.target.value)}
                                                placeholder="Add a comment for this decision..."
                                            />
                                            <div style={{ display: 'flex', gap: 10 }}>
                                                <button
                                                    className="btn btn-dark"
                                                    disabled={reviewSubmitting}
                                                    onClick={() => handleReviewDecision('approve')}
                                                >
                                                    {reviewSubmitting ? "Submitting..." : "Approve"}
                                                </button>
                                                <button
                                                    className="btn btn-outline-danger"
                                                    disabled={reviewSubmitting}
                                                    onClick={() => handleReviewDecision('reject')}
                                                >
                                                    {reviewSubmitting ? "Submitting..." : "Reject"}
                                                </button>
                                            </div>
                                        </>
                                    ) : (
                                        <p style={{ fontSize: 13, color: '#94a3b8' }}>
                                            Only enquiries with status "Submitted" can be reviewed. This one is currently "{enquiryStatus || '—'}".
                                        </p>
                                    )}

                                    {/* Read-only product list for the reviewer, same columns as the Product tab */}
                                    <div style={{ ...styles.tableContainer, padding: 0, overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 8, marginTop: 20 }}>
                                        <table className="table mb-0" style={styles.erpTable}>
                                            <thead>
                                                <tr style={{ backgroundColor: '#f8fafc' }}>
                                                    <th style={{ ...styles.erpTh, width: 40 }}>Item</th>
                                                    <th style={{ ...styles.erpTh, minWidth: 200 }}>Product Code</th>
                                                    <th style={{ ...styles.erpTh, minWidth: 180 }}>Description</th>
                                                    <th style={{ ...styles.erpTh, width: 80 }}>Fast Entry</th>
                                                    <th style={{ ...styles.erpTh, width: 70 }}>UOM</th>
                                                    <th style={{ ...styles.erpTh, width: 90 }}>Qty</th>
                                                    <th style={{ ...styles.erpTh, width: 110, whiteSpace: 'nowrap' }}>Range</th>
                                                    <th style={{ ...styles.erpTh, width: 70 }}>Currency</th>
                                                    <th style={{ ...styles.erpTh, textAlign: 'right', width: 90 }}>Unit Price</th>
                                                    <th style={{ ...styles.erpTh, textAlign: 'right', width: 100 }}>Total Price</th>
                                                    <th style={{ ...styles.erpTh, minWidth: 120 }}>Req. Date</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {items.length === 0 && (
                                                    <tr>
                                                        <td colSpan={11} style={{ textAlign: 'center', padding: '24px 16px', color: '#94a3b8', fontSize: 13 }}>
                                                            No products on this enquiry.
                                                        </td>
                                                    </tr>
                                                )}
                                                {items.map((row, idx) => (
                                                    <tr key={idx} style={{ verticalAlign: 'middle' }}>
                                                        <td style={{ ...styles.erpTd, textAlign: 'center', fontWeight: 600, color: '#64748b' }}>{idx + 1}</td>
                                                        <td style={styles.erpTd}>{row.product_code}</td>
                                                        <td style={{ ...styles.erpTd, fontSize: 12 }} title={row.description || ""}>{row.description}</td>
                                                        <td style={styles.erpTd}>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                                <span style={{ fontSize: 12, color: '#475569' }}>{row.product_class}</span>
                                                                {row.product_class && (
                                                                    <span onClick={() => handleOpenCharModal(idx, row)}
                                                                        style={{ cursor: 'pointer', color: '#1e293b', fontSize: 14, fontWeight: 900, lineHeight: 1 }}>▶</span>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td style={styles.erpTd}>{row.uom}</td>
                                                        <td style={styles.erpTd}>{row.quantity}</td>
                                                        <td style={{ ...styles.erpTd, whiteSpace: 'nowrap' }}>
                                                            <span style={{ fontSize: 12, fontWeight: 600, color: row.range ? '#16a34a' : '#94a3b8' }}>{row.range || '—'}</span>
                                                        </td>
                                                        <td style={styles.erpTd}>
                                                            <span style={{ fontSize: 12, fontWeight: 600, color: '#334155', background: '#f1f5f9', borderRadius: 4, padding: '2px 8px', display: 'inline-block', minWidth: 36, textAlign: 'center' }}>
                                                                {row.currency || '—'}
                                                            </span>
                                                        </td>
                                                        <td style={{ ...styles.erpTd, textAlign: 'right', fontWeight: 500 }}>{row.unit_price}</td>
                                                        <td style={{ ...styles.erpTd, textAlign: 'right', fontWeight: 600, color: '#0f172a' }}>{formatCurrency(row.total_price || 0)}</td>
                                                        <td style={styles.erpTd}>{formatDateLocal(row.req_date)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                            {items.length > 0 && (
                                                <tfoot>
                                                    <tr style={{ background: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
                                                        <td colSpan={9} style={{ ...styles.erpTd, textAlign: 'right', fontWeight: 700, color: '#475569', fontSize: 13 }}>TOTAL:</td>
                                                        <td style={{ ...styles.erpTd, textAlign: 'right', fontWeight: 700, color: '#0f172a', fontSize: 13 }}>
                                                            {formatCurrency(items.reduce((acc, r) => acc + (parseFloat(r.total_price) || 0), 0))}
                                                        </td>
                                                        <td></td>
                                                    </tr>
                                                </tfoot>
                                            )}
                                        </table>
                                    </div>
                                </div>
                            )}

                            {/* --- TAB: PRODUCTS --- */}
                            {activeTab === "product" && !isReviewOnly && (
                                <div>
                                    {modalMode !== 'view' && (
                                        <div>
                                            {/* Add Product button row */}
                                            <div className="d-flex justify-content-end mb-2">
                                                <button className="btn btn-sm btn-dark" onClick={handleAddItem}>+ Add Product</button>
                                            </div>
                                            {/* Inline filter row — always visible, no modal */}
                                            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 12px', marginBottom: 10 }}>
                                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, flexWrap: 'wrap' }}>
                                                    <span style={{ fontSize: 12, fontWeight: 600, color: '#64748b', whiteSpace: 'nowrap' }}></span>
                                                    {[
                                                        { key: 'category', opts: filterMasterData.categories, label: 'Category' },
                                                        { key: 'cableType', opts: filterMasterData.cableTypes, label: 'Cable Type' },
                                                        { key: 'code', opts: filterMasterData.codes, label: 'Code' },
                                                        { key: 'color', opts: filterMasterData.colours, label: 'Color' },
                                                        { key: 'noOfCores', opts: filterMasterData.cores, label: 'Cores' },
                                                        { key: 'nominalCrossSection', opts: filterMasterData.nominalCrossSections, label: 'Cross Section' },
                                                        { key: 'referenceOfStandard', opts: filterMasterData.referenceOdStandards, label: 'IS Std Ref' },
                                                        { key: 'voltage', opts: filterMasterData.voltages, label: 'Voltage' },
                                                        { key: 'construction', opts: filterMasterData.constructions, label: 'Construction' },
                                                    ].map(({ key, opts, label }) => (
                                                        <div
                                                            key={key}
                                                            title={
                                                                globalFilters[key] && globalFilters[key].length > 0
                                                                    ? globalFilters[key].map(o => o.label).join(', ')
                                                                    : undefined
                                                            }
                                                            style={{ minWidth: 120, flex: '1 1 110px', maxWidth: 180, display: 'flex', flexDirection: 'column', gap: 3 }}
                                                        >
                                                            <span style={{
                                                                fontSize: 11,
                                                                fontWeight: 600,
                                                                color: '#64748b',
                                                                whiteSpace: 'nowrap',
                                                                letterSpacing: '0.02em',
                                                                paddingLeft: 2
                                                            }}>
                                                                {label}
                                                            </span>
                                                            <Select
                                                                options={getFilterOptions(opts)}
                                                                value={globalFilters[key] && globalFilters[key].length > 0 ? globalFilters[key] : null}
                                                                isMulti
                                                                closeMenuOnSelect={false}
                                                                hideSelectedOptions={false}
                                                                onChange={selectedOpts => {
                                                                    const selected = selectedOpts || [];
                                                                    const updated = { ...globalFilters, [key]: selected };
                                                                    setGlobalFilters(updated);
                                                                    // apply instantly
                                                                    const anyActive = Object.values(updated).some(v => Array.isArray(v) ? v.length > 0 : v !== '');
                                                                    if (!anyActive) { setFilteredProductList(null); return; }
                                                                    const matchesFilter = (productVal, selectedVals) => {
                                                                        if (!selectedVals || selectedVals.length === 0) return true;
                                                                        return selectedVals.some(s => String(productVal || '').trim().toLowerCase() === s.value.toLowerCase());
                                                                    };
                                                                    const filtered = productList.filter(p =>
                                                                        matchesFilter(p.construction, updated.construction) &&
                                                                        matchesFilter(p.code, updated.code) &&
                                                                        matchesFilter(p.cableType, updated.cableType) &&
                                                                        matchesFilter(p.category, updated.category) &&
                                                                        matchesFilter(p.noOfCores, updated.noOfCores) &&
                                                                        matchesFilter(p.color, updated.color) &&
                                                                        matchesFilter(p.nominalCrossSection, updated.nominalCrossSection) &&
                                                                        matchesFilter(p.referenceOfStandard, updated.referenceOfStandard) &&
                                                                        matchesFilter(p.voltage, updated.voltage)
                                                                    );
                                                                    setFilteredProductList(filtered);
                                                                }}
                                                                isClearable
                                                                placeholder={`Select...`}
                                                                menuPortalTarget={document.body}
                                                                menuPosition="fixed"
                                                                styles={{
                                                                    control: (base) => ({ ...base, minHeight: 30, fontSize: '12px' }),
                                                                    valueContainer: (base) => ({ ...base, padding: '2px 6px', flexWrap: 'wrap' }),
                                                                    input: (base) => ({ ...base, margin: 0, padding: 0, fontSize: '12px' }),
                                                                    indicatorsContainer: (base) => ({ ...base, alignSelf: 'flex-start', paddingTop: 2 }),
                                                                    dropdownIndicator: (base) => ({ ...base, padding: 2 }),
                                                                    clearIndicator: (base) => ({ ...base, padding: 2 }),
                                                                    placeholder: (base) => ({ ...base, fontSize: '12px', color: '#94a3b8', fontWeight: 400 }),
                                                                    option: (base, state) => ({
                                                                        ...base,
                                                                        fontSize: '12px',
                                                                        fontWeight: 400,
                                                                        color: state.isSelected ? '#fff' : '#334155',
                                                                        backgroundColor: state.isSelected ? '#1e293b' : state.isFocused ? '#f1f5f9' : '#fff',
                                                                        padding: '6px 10px',
                                                                    }),
                                                                    multiValue: (base) => ({ ...base, fontSize: '11px', backgroundColor: '#e2e8f0', borderRadius: 4 }),
                                                                    multiValueLabel: (base) => ({ ...base, fontSize: '11px', color: '#334155', padding: '1px 4px' }),
                                                                    multiValueRemove: (base) => ({ ...base, color: '#64748b', ':hover': { backgroundColor: '#cbd5e1', color: '#1e293b' } }),
                                                                    menuPortal: (base) => ({ ...base, zIndex: 99999 }),
                                                                    menu: (base) => ({ ...base, fontSize: '12px' }),
                                                                }}
                                                            />
                                                        </div>
                                                    ))}
                                                    {filteredProductList && (
                                                        <button
                                                            className="btn btn-sm btn-outline-secondary"
                                                            style={{ fontSize: 11, whiteSpace: 'nowrap', height: 30, padding: '0 10px' }}
                                                            onClick={clearGlobalFilters}
                                                        >
                                                            ✕ Clear ({filteredProductList.length})
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    <div style={{ ...styles.tableContainer, padding: 0, overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 8 }}>
                                        <table className="table mb-0" style={styles.erpTable}>
                                            <thead>
                                                <tr style={{ backgroundColor: '#f8fafc' }}>
                                                    <th style={{ ...styles.erpTh, width: 40 }}>Item</th>
                                                    <th style={{ ...styles.erpTh, minWidth: 200 }}>Product Code</th>
                                                    <th style={{ ...styles.erpTh, minWidth: 180 }}>Description</th>
                                                    <th style={{ ...styles.erpTh, width: 80 }}>Fast Entry</th>
                                                    <th style={{ ...styles.erpTh, width: 70 }}>UOM</th>
                                                    <th style={{ ...styles.erpTh, width: 90 }}>Qty <span style={{ color: 'red' }}>*</span></th>
                                                    <th style={{ ...styles.erpTh, width: 110, whiteSpace: 'nowrap' }}>Range</th>
                                                    <th style={{ ...styles.erpTh, width: 70 }}>Currency</th>
                                                    <th style={{ ...styles.erpTh, textAlign: 'right', width: 90 }}>Unit Price</th>
                                                    <th style={{ ...styles.erpTh, textAlign: 'right', width: 100 }}>Total Price</th>
                                                    <th style={{ ...styles.erpTh, minWidth: 120 }}>Req. Date <span style={{ color: 'red' }}>*</span></th>
                                                    {modalMode !== 'view' && <th style={{ ...styles.erpTh, width: 44 }}></th>}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {items.length === 0 && (
                                                    <tr>
                                                        <td colSpan={modalMode !== 'view' ? 12 : 11} style={{ textAlign: 'center', padding: '32px 16px', color: '#94a3b8', fontSize: 13 }}>
                                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                                                                <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" fill="#cbd5e1" viewBox="0 0 16 16"><path d="M0 2.5A.5.5 0 0 1 .5 2H2a.5.5 0 0 1 .485.379L2.89 4H14.5a.5.5 0 0 1 .485.621l-1.5 6A.5.5 0 0 1 13 11H4a.5.5 0 0 1-.485-.379L1.61 3H.5a.5.5 0 0 1-.5-.5zM3.14 5l1.25 5h8.22l1.25-5H3.14z" /></svg>
                                                                <span>No products added yet. Click <b>+ Add Product</b> to begin.</span>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                                {items.map((row, idx) => {
                                                    const isFromRecords = !!row._fromRecords;
                                                    const isEditable = modalMode !== 'view';
                                                    const selectedProductOpt = row.product_code
                                                        ? productList.find(o => o.value === row.product_code)
                                                        || { value: row.product_code, label: row.product_code, __isNew__: true }
                                                        : null;
                                                    return (
                                                        <tr key={idx} style={{ verticalAlign: 'middle' }}>
                                                            <td style={{ ...styles.erpTd, textAlign: 'center', fontWeight: 600, color: '#64748b' }}>
                                                                {modalMode === 'view' && !isFieldVisible('item_no') ? <MaskedValue /> : idx + 1}
                                                            </td>

                                                            {/* Product Code — CreatableSelect with "code - description" labels */}
                                                            <td style={{ ...styles.erpTd, overflow: 'visible', padding: '4px 6px' }}>
                                                                {isEditable ? (
                                                                    <div style={{ flex: 1, minWidth: '180px' }}>
                                                                        <CreatableSelect
                                                                            options={filteredProductList || productList}
                                                                            value={selectedProductOpt}
                                                                            onChange={(opt) => {
                                                                                if (!opt) {
                                                                                    const u = [...items];
                                                                                    u[idx] = { product_code: '', description: '', uom: '', quantity: '', unit_price: '', total_price: 0, req_date: '', product_class: '', characteristics: [], _fromRecords: false };
                                                                                    setItems(u);
                                                                                    return;
                                                                                }
                                                                                if (opt.__isNew__) {
                                                                                    const u = [...items];
                                                                                    u[idx] = { ...u[idx], product_code: opt.value, description: '', uom: '', unit_price: '', total_price: 0, product_class: '', characteristics: [], _fromRecords: false };
                                                                                    setItems(u);
                                                                                } else {
                                                                                    handleProductChange(idx, opt);
                                                                                }
                                                                            }}
                                                                            formatCreateLabel={(val) => `Use custom: "${val}"`}
                                                                            filterOption={(option, inputValue) => {
                                                                                if (!inputValue) return true;
                                                                                const q = inputValue.toLowerCase();
                                                                                return option.label.toLowerCase().includes(q) || (option.value || '').toLowerCase().includes(q);
                                                                            }}
                                                                            placeholder="Code or description..."
                                                                            isSearchable
                                                                            isClearable
                                                                            isDisabled={isFieldLocked('product_code')}
                                                                            menuPortalTarget={document.body}
                                                                            menuPosition="fixed"
                                                                            styles={productSelectStyles}
                                                                        />
                                                                    </div>
                                                                ) : modalMode === 'view' && !isFieldVisible('product_code') ? (
                                                                    <MaskedValue />
                                                                ) : (
                                                                    <span style={{ fontWeight: 500 }}>{row.product_code}</span>
                                                                )}
                                                            </td>

                                                            {/* Description — disabled if from records */}
                                                            <td style={{ ...styles.erpTd, padding: '4px 6px', position: 'relative' }}>
                                                                {isEditable ? (
                                                                    <div style={{ position: 'relative' }} title={row.description || ""}>
                                                                        <input
                                                                            className="form-control"
                                                                            style={{ ...styles.compactInput, backgroundColor: (isFromRecords || isFieldLocked('item_description')) ? '#f1f5f9' : '#fff', cursor: (isFromRecords || isFieldLocked('item_description')) ? 'not-allowed' : 'text' }}
                                                                            value={row.description}
                                                                            disabled={isFromRecords || isFieldLocked('item_description')}
                                                                            onChange={e => !isFromRecords && !isFieldLocked('item_description') && handleItemChange(idx, 'description', e.target.value)}
                                                                            title={row.description || (isFromRecords ? "Auto-filled from product records" : "")}
                                                                        />
                                                                    </div>
                                                                ) : modalMode === 'view' && !isFieldVisible('item_description') ? (
                                                                    <MaskedValue />
                                                                ) : (
                                                                    <span style={{ fontSize: 12 }} title={row.description || ""}>{row.description}</span>
                                                                )}
                                                            </td>

                                                            {/* Class + Characteristics trigger (Fast Entry column) */}
                                                            <td style={styles.erpTd}>
                                                                {modalMode === 'view' && !isFieldVisible('fast_entry') ? (
                                                                    <MaskedValue />
                                                                ) : (
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                                        <span style={{ fontSize: 12, color: '#475569' }}>{row.product_class}</span>
                                                                        {row.product_class && (
                                                                            <span onClick={() => handleOpenCharModal(idx, row)}
                                                                                style={{ cursor: 'pointer', color: '#1e293b', fontSize: 14, fontWeight: 900, lineHeight: 1 }}>▶</span>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </td>

                                                            {/* UOM — disabled if from records */}
                                                            <td style={{ ...styles.erpTd, padding: '4px 6px' }}>
                                                                {isEditable ? (
                                                                    <input
                                                                        className="form-control"
                                                                        style={{ ...styles.compactInput, width: 60, backgroundColor: (isFromRecords || isFieldLocked('uom')) ? '#f1f5f9' : '#fff', cursor: (isFromRecords || isFieldLocked('uom')) ? 'not-allowed' : 'text' }}
                                                                        value={row.uom}
                                                                        disabled={isFromRecords || isFieldLocked('uom')}
                                                                        onChange={e => !isFromRecords && !isFieldLocked('uom') && handleItemChange(idx, 'uom', e.target.value)}
                                                                        title={isFromRecords ? "Auto-filled from product records" : ""}
                                                                    />
                                                                ) : modalMode === 'view' && !isFieldVisible('uom') ? (
                                                                    <MaskedValue />
                                                                ) : (
                                                                    <span>{row.uom}</span>
                                                                )}
                                                            </td>

                                                            {/* Qty — disabled when product has class (set via characteristics) */}
                                                            <td style={{ ...styles.erpTd, padding: '4px 6px' }}>
                                                                {isEditable ? (
                                                                    <input type="number" className="form-control"
                                                                        style={{ ...styles.compactInput, width: 74, backgroundColor: (row.product_class || isFieldLocked('qty')) ? '#f1f5f9' : '#fff', cursor: (row.product_class || isFieldLocked('qty')) ? 'not-allowed' : 'text' }}
                                                                        value={row.quantity}
                                                                        disabled={!!row.product_class || isFieldLocked('qty')}
                                                                        title={row.product_class ? 'Qty is calculated via Characteristics (▶)' : ''}
                                                                        onChange={e => !isFieldLocked('qty') && handleItemChange(idx, 'quantity', e.target.value)} />
                                                                ) : modalMode === 'view' && !isFieldVisible('qty') ? (
                                                                    <MaskedValue />
                                                                ) : row.quantity}
                                                            </td>

                                                            {/* Range — read-only, calculated from Over/Under Tolerances via Characteristics (▶) */}
                                                            <td style={{ ...styles.erpTd, padding: '4px 6px', whiteSpace: 'nowrap' }}>
                                                                {modalMode === 'view' && !isFieldVisible('range') ? (
                                                                    <MaskedValue />
                                                                ) : (
                                                                    <span style={{ fontSize: 12, fontWeight: 600, color: row.range ? '#16a34a' : '#94a3b8', whiteSpace: 'nowrap' }}>
                                                                        {row.range || '—'}
                                                                    </span>
                                                                )}
                                                            </td>

                                                            {/* Currency — read-only, auto-filled from product */}
                                                            <td style={{ ...styles.erpTd, padding: '4px 6px' }}>
                                                                {modalMode === 'view' && !isFieldVisible('currency') ? (
                                                                    <MaskedValue />
                                                                ) : (
                                                                    <span style={{
                                                                        fontSize: 12,
                                                                        fontWeight: 600,
                                                                        color: '#334155',
                                                                        background: '#f1f5f9',
                                                                        borderRadius: 4,
                                                                        padding: '2px 8px',
                                                                        display: 'inline-block',
                                                                        minWidth: 36,
                                                                        textAlign: 'center'
                                                                    }}>
                                                                        {row.currency || '—'}
                                                                    </span>
                                                                )}
                                                            </td>

                                                            {/* Unit Price */}
                                                            <td style={{ ...styles.erpTd, textAlign: 'right', padding: '4px 6px' }}>
                                                                {isEditable ? (
                                                                    <input type="number" className="form-control no-spinner"
                                                                        style={{ ...styles.compactInput, width: 80, textAlign: 'right', MozAppearance: 'textfield', backgroundColor: '#e9ecef' }}
                                                                        value={row.unit_price}
                                                                        disabled={true}
                                                                        title="Unit Price is read-only" />
                                                                ) : modalMode === 'view' && !isFieldVisible('unit_price') ? (
                                                                    <MaskedValue />
                                                                ) : <span style={{ fontWeight: 500 }}>{row.unit_price}</span>}
                                                            </td>

                                                            {/* Total Price */}
                                                            <td style={{ ...styles.erpTd, textAlign: 'right', fontWeight: 600, color: '#0f172a' }}>
                                                                {modalMode === 'view' && !isFieldVisible('total_price') ? (
                                                                    <MaskedValue />
                                                                ) : (
                                                                    formatCurrency(row.total_price || 0)
                                                                )}
                                                            </td>


                                                            {/* Req Date */}
                                                            <td style={{ ...styles.erpTd, padding: '4px 6px' }}>
                                                                {isEditable ? (
                                                                    <DatePicker
                                                                        className="form-control"
                                                                        wrapperClassName="w-100"
                                                                        style={{ ...styles.compactInput, minWidth: 110 }}
                                                                        dateFormat="dd/MM/yyyy"
                                                                        placeholderText="dd/mm/yyyy"
                                                                        disabled={isFieldLocked('req_date')}
                                                                        selected={row.req_date ? new Date(row.req_date.substring(0, 10)) : null}
                                                                        onChange={date => {
                                                                            if (!date) {
                                                                                handleItemChange(idx, 'req_date', '');
                                                                                return;
                                                                            }
                                                                            const yyyy = date.getFullYear();
                                                                            const mm = String(date.getMonth() + 1).padStart(2, '0');
                                                                            const dd = String(date.getDate()).padStart(2, '0');
                                                                            handleItemChange(idx, 'req_date', `${yyyy}-${mm}-${dd}`);
                                                                        }}
                                                                    />
                                                                ) : modalMode === 'view' && !isFieldVisible('req_date') ? (
                                                                    <MaskedValue />
                                                                ) : formatDateLocal(row.req_date)}
                                                            </td>
                                                            {/* Delete — Dustbin icon */}
                                                            {isEditable && (
                                                                <td style={{ ...styles.erpTd, textAlign: 'center', padding: '4px 6px' }}>
                                                                    <button onClick={() => handleRemoveItem(idx)}
                                                                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: 4, borderRadius: 4, transition: 'background 0.15s' }}
                                                                        title="Remove row"
                                                                        onMouseEnter={e => e.currentTarget.style.background = '#fee2e2'}
                                                                        onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                                                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                                                                            <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z" />
                                                                            <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z" />
                                                                        </svg>
                                                                    </button>
                                                                </td>
                                                            )}
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                            {items.length > 0 && (
                                                <tfoot>
                                                    <tr style={{ background: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
                                                        <td colSpan={9} style={{ ...styles.erpTd, textAlign: 'right', fontWeight: 700, color: '#475569', fontSize: 13 }}>TOTAL:</td>
                                                        <td style={{ ...styles.erpTd, textAlign: 'right', fontWeight: 700, color: '#0f172a', fontSize: 13 }}>
                                                            {formatCurrency(items.reduce((acc, r) => acc + (parseFloat(r.total_price) || 0), 0))}
                                                        </td>
                                                        <td colSpan={modalMode !== 'view' ? 2 : 1}></td>
                                                    </tr>
                                                </tfoot>
                                            )}
                                        </table>
                                    </div>
                                </div>
                            )}

                            {/* --- TAB: DOCS --- */}
                            {activeTab === "docs" && !isReviewOnly && (
                                <div style={{ background: '#fff', borderRadius: 8, padding: 16, border: '1px solid #e2e8f0', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>

                                    {/* Upload area — visible in create AND edit AND view modes */}
                                    <div style={{ marginBottom: 16 }}>
                                        <label
                                            htmlFor="docFileInput"
                                            style={{
                                                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                                                border: '2px dashed #cbd5e1', borderRadius: 8, padding: '18px 12px',
                                                cursor: 'pointer', background: '#f8fafc', transition: 'background 0.2s'
                                            }}
                                        >
                                            <span style={{ fontSize: 22 }}>📁</span>
                                            <span style={{ fontSize: 13, color: '#475569', fontWeight: 500, marginTop: 4 }}>Click to attach documents</span>
                                            <span style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>PDF, Word, Excel, Images, etc. (max {maxEnquiryDocSizeKb} KB per file)</span>
                                            <input
                                                id="docFileInput"
                                                type="file"
                                                ref={docFileInputRef}
                                                multiple
                                                style={{ display: 'none' }}
                                                onChange={async (e) => {
                                                    const rawFiles = Array.from(e.target.files);
                                                    e.target.value = "";
                                                    if (!rawFiles.length) return;

                                                    const files = filterFilesWithinSizeLimit(rawFiles);
                                                    if (!files.length) return;

                                                    const targetId = enquiryId || "DRAFT";
                                                    const formData = new FormData();
                                                    files.forEach(f => formData.append("docs", f));

                                                    try {
                                                        const res = await fetch(`${ORDER_API}/enquiry/upload-docs/${targetId}`,{ headers: authHeaders(), method: "POST",
                                                            body: formData });
                                                        if (!res.ok) throw new Error(await res.text());
                                                        const result = await res.json();
                                                        const saved = result.files.map(f => ({
    name: f.originalname,
    filename: f.filename,
    googleFileId: f.googleFileId,
    webViewLink: f.webViewLink,
    size: f.size,
    type: f.mimetype,
    uploadedAt: new Date().toLocaleString(),
    enquiryId: targetId
}));
                                                        setDocFiles(prev => [...prev, ...saved]);
                                                        toast.success(`${saved.length} file(s) uploaded to Desktop/Files/enquiry/${targetId}`);
                                                    } catch (err) {
                                                        toast.error("File upload failed: " + err.message);
                                                    }
                                                }}
                                            />
                                        </label>
                                    </div>

                                    {/* File list */}
                                    {docFiles.length === 0 ? (
                                        <div style={{ textAlign: 'center', padding: '28px 0', color: '#94a3b8', fontSize: 13 }}>
                                            <div style={{ fontSize: 32, marginBottom: 8 }}></div>
                                            No documents attached yet.
                                        </div>
                                    ) : (
                                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                                            <thead>
                                                <tr>
                                                    <th style={styles.erpTh}>#</th>
                                                    <th style={styles.erpTh}>File Name</th>
                                                    <th style={styles.erpTh}>Type</th>
                                                    <th style={styles.erpTh}>Size</th>
                                                    <th style={styles.erpTh}>Uploaded At</th>
                                                    <th style={styles.erpTh}>Download</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {docFiles.map((doc, idx) => (
                                                    <tr key={idx} style={{ background: idx % 2 === 0 ? '#fff' : '#f8fafc' }}>
                                                        <td style={styles.erpTd}>{idx + 1}</td>
                                                        <td style={styles.erpTd}>
                                                            <span style={{ marginRight: 6 }}>{getFileIcon(doc.name, doc.type)}</span>
                                                            <span style={{ color: '#1e293b', fontWeight: 500 }}>{doc.name}</span>
                                                        </td>
                                                        <td style={styles.erpTd}>
                                                            <span style={{
                                                                background: '#f1f5f9', borderRadius: 4, padding: '2px 7px',
                                                                fontSize: 11, color: '#475569', fontWeight: 600
                                                            }}>
                                                                {doc.type
                                                                    ? doc.type.split('/').pop().toUpperCase()
                                                                    : (doc.name?.split('.').pop() || '—').toUpperCase()}
                                                            </span>
                                                        </td>
                                                        <td style={styles.erpTd}>
                                                            {doc.size
                                                                ? doc.size > 1024 * 1024
                                                                    ? `${(doc.size / (1024 * 1024)).toFixed(2)} MB`
                                                                    : `${(doc.size / 1024).toFixed(1)} KB`
                                                                : '—'}
                                                        </td>
                                                        <td style={styles.erpTd}>{doc.uploadedAt || '—'}</td>
                                                        <td style={styles.erpTd}>

                                                            <a   href={doc.webViewLink}
    target="_blank"
    rel="noopener noreferrer"
                                                                style={{
                                                                    display: 'inline-flex', alignItems: 'center', gap: 4,
                                                                    color: '#2563eb', fontWeight: 600, fontSize: 12,
                                                                    textDecoration: 'none', padding: '3px 10px',
                                                                    border: '1px solid #bfdbfe', borderRadius: 5,
                                                                    background: '#eff6ff'
                                                                }}
                                                            >
                                                                ⬇ Download
                                                            </a>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                            )}

                            {/* --- TAB: CHANGE LOG --- */}
                            {activeTab === "changeLog" && !isReviewOnly && (
                                <div style={{ background: "#fff", borderRadius: 8, padding: 12, border: "1px solid #e2e8f0", boxShadow: "0 1px 4px rgba(0,0,0,0.08)" }}>
                                    <div style={{ overflowX: "auto" }}>
                                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                                            <thead>
                                                <tr style={{ background: "#f8fafc" }}>
                                                    <th style={styles.erpTh}>Date & Time</th>
                                                    <th style={styles.erpTh}>User</th>
                                                    <th style={styles.erpTh}>Action</th>
                                                    <th style={styles.erpTh}>Status</th>
                                                    <th style={styles.erpTh}>Field</th>
                                                    <th style={styles.erpTh}>Previous Value</th>
                                                    <th style={styles.erpTh}>Modified Value</th>
                                                    <th style={styles.erpTh}>Comment</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {enquiryLogs.length > 0 ? enquiryLogs.map((log) => (
                                                    <tr key={log.id}>
                                                        <td style={styles.erpTd}>{formatChangeLogDateTime(log.created_at)}</td>
                                                        <td style={styles.erpTd}>{log.created_by || "Admin"}</td>
                                                        <td style={styles.erpTd}>{log.action_type || "-"}</td>
                                                        <td style={styles.erpTd}>{log.doc_status || "-"}</td>
                                                        <td style={styles.erpTd}>{log.field_name || "-"}</td>
                                                        <td style={styles.erpTd}>{log.old_value || "-"}</td>
                                                        <td style={{ ...styles.erpTd, fontWeight: 700, color: "#0f172a" }}>{log.new_value || "-"}</td>
                                                        <td style={styles.erpTd}>{log.comment || "-"}</td>
                                                    </tr>
                                                )) : (
                                                    <tr>
                                                        <td colSpan="8" style={{ textAlign: "center", padding: 20, color: "#64748b" }}>
                                                            No change logs available
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {/* --- Characteristics Modal Component --- */}
                            <CharacteristicsModal
                                modalVisible={modalVisible}
                                selectedCharacteristics={selectedCharacteristics}
                                isEditing={modalMode === 'create' || modalMode === 'edit'}
                                handleChange={(idx, val) => {
                                    if (modalMode === 'view') return;
                                    const charName = selectedCharacteristics[idx].characteristic.toLowerCase();
                                    if ((charName === 'factor' || charName === 'length') && val !== "" && !/^\d*\.?\d*$/.test(val)) {
                                        return;
                                    }
                                    const u = [...selectedCharacteristics];
                                    u[idx].value = val;
                                    setSelectedCharacteristics(u);
                                }}
                                handleToggleTolerance={handleToggleTolerance}
                                onAdd={handleAddDrumGroup}
                                onRemove={handleRemoveDrumGroup}
                                handleClose={() => handleSaveCharModal(selectedCharsRef.current)} />

                        </div>

                        {/* Modal Footer */}
                        <div style={styles.modalFooter}>
                            {modalMode === 'create' && (
                                <>
                                    <button className="btn btn-dark" onClick={handleCreateInquiry} disabled={enquiryId !== ""}>Create</button>
                                    <button
                                        className="btn btn-dark"
                                        disabled={!enquiryId}
                                        title={!enquiryId ? "Click Create first to generate an Enquiry ID" : ""}
                                        onClick={() => {
                                            if (!enquiryId) return toast.error("Generate ID first!");
                                            setShowCommentModal(true);
                                        }}>Submit</button>
                                </>
                            )}
                            {modalMode === 'view' && (
                                <>
                                    {enquiryStatus === 'Draft' && !isDisplayOnly && !isReviewOnly && (
                                        <button
                                            className="btn btn-dark"
                                            onClick={() => {
                                                setSubmitComment("");
                                                setShowCommentModal(true);
                                            }}
                                        >
                                            Submit
                                        </button>
                                    )}
                                    {!isDisplayOnly && !isReviewOnly && (
                                        <button
                                            className="btn btn-dark"
                                            onClick={() => setModalMode("edit")}
                                        >
                                            Edit
                                        </button>
                                    )}
                                </>
                            )}

                            {modalMode === 'edit' && (
                                <>
                                    <button
                                        className="btn btn-dark"
                                        disabled={!enquiryId}
                                        title={!enquiryId ? "Enquiry must be created first" : ""}
                                        onClick={handleUpdateInquiry}
                                    >
                                        Submit
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Excel Upload Modal */}
            {showExcelModal && (
                <div style={{ ...styles.modalBackdrop, zIndex: 1060 }}>
                    <div style={{ background: '#fff', borderRadius: '12px', width: '90%', maxWidth: '480px', boxShadow: '0 8px 32px rgba(0,0,0,0.18)', overflow: 'hidden' }}>
                        {/* Header */}
                        <div style={{ padding: '18px 24px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc' }}>
                            <div style={{ fontWeight: 700, fontSize: '1rem', color: '#1e293b' }}>📊 Excel Upload</div>
                            <button style={styles.iconBtn} onClick={() => setShowExcelModal(false)}>✕</button>
                        </div>

                        {/* Body */}
                        <div style={{ padding: '24px' }}>

                            {/* Step 1 – Download Template */}
                            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '16px', marginBottom: '20px' }}>
                                <div style={{ fontWeight: 600, fontSize: 13, color: '#166534', marginBottom: 6 }}>Step 1 — Download Template</div>
                                <p style={{ fontSize: 12, color: '#4b5563', margin: '0 0 12px 0' }}>
                                    Download the template file and fill in your product details. Make sure the column headers match exactly.
                                </p>
                                <button
                                    className="btn btn-sm"
                                    style={{ background: '#16a34a', color: '#fff', fontWeight: 600, fontSize: 12, display: 'flex', alignItems: 'center', gap: 6, border: 'none', borderRadius: 6, padding: '7px 16px' }}
                                    onClick={handleDownloadTemplate}
                                >
                                    ⬇️ Download Template
                                </button>
                            </div>

                            {/* Step 2 – Upload File */}
                            <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '16px' }}>
                                <div style={{ fontWeight: 600, fontSize: 13, color: '#1d4ed8', marginBottom: 6 }}>Step 2 — Upload Filled File</div>
                                <p style={{ fontSize: 12, color: '#4b5563', margin: '0 0 12px 0' }}>
                                    Upload the completed Excel or CSV file to import your product line items.
                                </p>

                                {/* Drop zone */}
                                <label
                                    htmlFor="excelFileInput"
                                    style={{
                                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                                        border: '2px dashed #93c5fd', borderRadius: 8, padding: '20px 12px', cursor: 'pointer',
                                        background: salesFile ? '#dbeafe' : '#fff', transition: 'background 0.2s', marginBottom: 12
                                    }}
                                >
                                    <span style={{ fontSize: 28 }}>{salesFile ? '✅' : '📁'}</span>
                                    <span style={{ fontSize: 12, color: '#374151', marginTop: 6, fontWeight: salesFile ? 600 : 400 }}>
                                        {salesFile ? salesFile.name : 'Click to browse or drag & drop'}
                                    </span>
                                    <span style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>Supports .xls, .xlsx, .csv</span>
                                    <input
                                        id="excelFileInput"
                                        type="file"
                                        ref={salesFileInputRef}
                                        accept=".xls,.xlsx,.csv"
                                        style={{ display: 'none' }}
                                        onChange={(e) => setSalesFile(e.target.files[0])}
                                    />
                                </label>

                                {salesFile && (
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 12, color: '#374151', background: '#fff', border: '1px solid #bfdbfe', borderRadius: 6, padding: '6px 10px' }}>
                                        <span>📄 {salesFile.name}</span>
                                        <button onClick={() => setSalesFile(null)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 14, lineHeight: 1 }}>✕</button>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Footer */}
                        <div style={{ padding: '14px 24px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end', gap: 10, background: '#f8fafc' }}>
                            <button className="btn btn-outline-secondary btn-sm" onClick={() => setShowExcelModal(false)}>Cancel</button>
                            <button
                                className="btn btn-dark btn-sm px-4"
                                onClick={handleSalesFileSave}
                                disabled={!salesFile || excelUploading}
                            >
                                {excelUploading ? 'Uploading...' : 'Upload & Import'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Comment Modal */}
            {showCommentModal && (
                <div style={styles.modalBackdrop}>
                    <div style={{ ...styles.modal, maxWidth: "600px", zIndex: 1060 }}>
                        <div style={styles.modalHeader}>
                            <div style={{ fontWeight: 600, fontSize: '18px', color: '#1e293b' }}>Submit Comment <span style={{ color: '#dc3545' }}>*</span></div>
                            <button style={styles.iconBtn} onClick={() => setShowCommentModal(false)}>✕</button>
                        </div>
                        <div style={styles.modalBody}>
                            <textarea value={submitComment} onChange={(e) => setSubmitComment(e.target.value)} rows="4" placeholder="Enter your comment here... (required)" style={{ width: "100%", padding: "10px", borderRadius: "5px", border: submitComment.trim() ? "1px solid #ced4da" : "1px solid #dc3545", resize: "none", fontSize: "13px" }} />
                            {!submitComment.trim() && (
                                <div style={{ color: "#dc3545", fontSize: "12px", marginTop: "4px" }}>Comment is required.</div>
                            )}
                        </div>
                        <div style={styles.modalFooter}>
                            <button className="btn btn-outline-secondary" onClick={() => setShowCommentModal(false)}>Cancel</button>
                            <button className="btn btn-dark" onClick={handleFinalSubmit} disabled={!submitComment.trim()} style={{ opacity: submitComment.trim() ? 1 : 0.6, cursor: submitComment.trim() ? "pointer" : "not-allowed" }}>Submit</button>
                        </div>
                    </div>
                </div>
            )}

            {/* ===================== DELETE CONFIRM MODAL ===================== */}
            {showDeleteConfirm && (
                <div style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    width: "100vw",
                    height: "100vh",
                    background: "rgba(0,0,0,0.5)",
                    zIndex: 1070,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center"
                }}>
                    <div style={{
                        background: "#fff",
                        width: "420px",
                        borderRadius: 14,
                        padding: "36px 32px",
                        boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
                        textAlign: "center",
                        position: "relative"
                    }}>
                        {/* CLOSE */}
                        <button
                            onClick={() => {
                                setShowDeleteConfirm(false);
                                setEnquiryToDelete(null);
                            }}
                            style={{
                                position: "absolute",
                                top: 16,
                                right: 18,
                                background: "transparent",
                                border: "none",
                                fontSize: 18,
                                cursor: "pointer",
                                color: "#94a3b8"
                            }}
                        >
                            ✖
                        </button>

                        {/* ICON BADGE */}
                        <div style={{
                            width: 64,
                            height: 64,
                            borderRadius: "50%",
                            background: "#ef4444",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            margin: "0 auto 20px",
                            fontSize: 28,
                            color: "#fff",
                            fontWeight: "bold"
                        }}>
                            🗑
                        </div>

                        {/* TITLE */}
                        <h4 style={{
                            margin: "0 0 12px",
                            fontSize: "19px",
                            fontWeight: 700,
                            color: "#1e293b"
                        }}>
                            Delete Enquiry
                        </h4>

                        {/* MESSAGE */}
                        <p style={{
                            fontSize: 14,
                            color: "#64748b",
                            marginBottom: 28,
                            lineHeight: 1.5
                        }}>
                            Delete enquiry "{enquiryToDelete}"? This will hide it from the list.
                        </p>

                        {/* ACTIONS */}
                        <div style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 10
                        }}>
                            <button
                                onClick={confirmDeleteEnquiry}
                                style={{
                                    padding: "12px 16px",
                                    borderRadius: 10,
                                    border: "none",
                                    background: "#ef4444",
                                    color: "#fff",
                                    cursor: "pointer",
                                    fontWeight: 600,
                                    fontSize: 14,
                                    width: "100%"
                                }}
                            >
                                Yes, Delete Enquiry
                            </button>

                            <button
                                onClick={() => {
                                    setShowDeleteConfirm(false);
                                    setEnquiryToDelete(null);
                                }}
                                style={{
                                    padding: "10px 16px",
                                    borderRadius: 10,
                                    border: "none",
                                    background: "transparent",
                                    color: "#334155",
                                    cursor: "pointer",
                                    fontWeight: 500,
                                    fontSize: 14,
                                    width: "100%"
                                }}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <ToastContainer position="top-right" autoClose={3000} />
        </Layout>
    );
}

// Reusable compact styles for product code CreatableSelect
const productSelectStyles = {
    control: (base) => ({ ...base, minHeight: 32, height: 32, fontSize: '13px', minWidth: 180 }),
    valueContainer: (base) => ({ ...base, height: 32, padding: '0 8px' }),
    input: (base) => ({ ...base, margin: 0, padding: 0 }),
    indicatorsContainer: (base) => ({ ...base, height: 32 }),
    dropdownIndicator: (base) => ({ ...base, padding: 2 }),
    menuPortal: (base) => ({ ...base, zIndex: 99999 }),
    menu: (base) => ({ ...base, fontSize: '13px' }),
    menuList: (base) => ({ ...base, padding: 4 }),
    option: (base, state) => ({
        ...base,
        fontSize: '13px',
        fontWeight: 400,
        color: state.isSelected ? '#fff' : '#334155',
        backgroundColor: state.isSelected ? '#475569' : state.isFocused ? '#f1f5f9' : '#fff',
        padding: '8px 12px',
        cursor: 'pointer',
    }),
};

const styles = {
    compactLabel: { fontSize: "12px", fontWeight: 600, marginBottom: "2px", color: '#334155' },
    compactInput: { fontSize: "13px", height: "34px", padding: "4px 8px" },
    compactTabBtn: { fontSize: "13px", padding: "4px 10px" },
    tableContainer: { background: "#ffffff", borderRadius: 8, padding: 12, boxShadow: "0 1px 4px rgba(0,0,0,0.08)" },
    erpTable: { width: "100%", tableLayout: "auto", borderCollapse: "collapse" },
    erpTh: { background: "#f8fafc", fontSize: 12, fontWeight: 600, padding: "10px 12px", borderBottom: "1px solid #e5e7eb", color: "#1e293b", whiteSpace: "nowrap" },
    erpTd: { fontSize: 13, padding: "10px 12px", borderBottom: "1px solid #f1f5f9", color: "#334155", verticalAlign: "middle" },
    filterCard: { background: '#ffffff', borderRadius: 8, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', marginBottom: 18 },
    filterLabel: { fontSize: 12, fontWeight: 600, marginBottom: 6, color: '#334155' },
    filterSelect: { height: 36, fontSize: 13 },
    pageTitleBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 },
    pageTitle: { fontSize: 20, fontWeight: 700, color: '#1e293b' },
    pageSubtitle: { fontSize: 12, color: '#64748b' },
    primaryBtn: { paddingLeft: 14, paddingRight: 14 },
    modalBackdrop: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1050, padding: '20px' },
    modal: { background: '#fff', borderRadius: '8px', width: '90%', maxWidth: '1300px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
    modalHeader: { padding: '15px 20px', borderBottom: '1px solid #ddd', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    iconBtn: { background: 'transparent', border: 'none', fontSize: '16px', cursor: 'pointer' },
    modalBody: { padding: '20px', overflowY: 'auto', flex: 1 },
    modalFooter: { padding: '15px 20px', borderTop: '1px solid #ddd', display: 'flex', justifyContent: 'flex-end', gap: '10px' },
    tabs: { display: 'flex', gap: 8, margin: '14px 0' },
};

export default Enquiry;